import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigationType, NavigationType } from "react-router-dom";
import { Money, TicketForm } from "@arema/components";
import { Button, Field, Groupper, Input, Message, Header, Table, Stat, Toolbar, Icon, IconName, Label, Modal, useFrontier, Dropdown, Checkbox } from 'react-frontier2'
import { addCommas, bindClick, bindFormChange, formatSeatNumber, fromUnix, getNow, getNowLocal, groupBy, toggleArray, uniqueArray } from "@arema/components/Util";
import { DeliveryMethod, PaymentMethod, QuestionType, SetLoading, TicketQuestion, TicketFormPayload, Location, SeatmapSeat, PaymentMethods, EventDateData } from "@arema/components/Classes";
import { Order, Ticket, TicketUse, CancelReason, DepositType, RefundType } from '../../AdminClasses';
import { useTitle, useValidator } from "@arema/components/Hooks";
import { UpdateSeatTicketModal, PaymentInfoModal, OrderInvoicesModal, LocationModal, OrderEmailsModal, SearchPaymentMethodDropdown, UserLog, UserLogModal, TicketPrintsModal, UpdateTicketDateModal } from "../../components";
import { useUser } from "../../AdminHooks";
import { useTranslation } from "react-i18next";
import Validator from "@arema/components/Validator";
import moment from "moment";
import API from "../../API";
import classNames from "classnames";
import UserAccess from "../../UserAccess";

import '../../style/order.scss';

interface ToolbarItemProps{
	onClick?: ()=>void,
	text: string,
	icon: IconName,
	style?: React.CSSProperties
}
var ToolbarItem = (props: ToolbarItemProps)=>{
	return <div className="item" onClick={props.onClick} style={props.style}>
		<Icon name={props.icon} style={{ lineHeight: '14px', marginRight: 10 }} /> {props.text}
	</div>	
}

var TicketItem = (props: { ticket: Ticket, price?: boolean })=><>
	<div className="left">
		<div style={{ fontWeight: 'bold' }}>{props.ticket.ticket_hash}</div>
		<div style={{ fontSize: 14 }}>{props.ticket.section_name} - {props.ticket.price_name}</div>
	</div>
	{props.ticket.seat_id ? (
		<div className="right" style={{ textAlign: 'right' }}>
			<div style={{ fontWeight: 'bold', fontSize: 18 }}>{formatSeatNumber(props.ticket.seat_row, props.ticket.seat_number)}</div>
			{props.ticket.seat_section && props.ticket.seat_section.length>0 ? (
				<div style={{ fontSize: 12 }}>{props.ticket.seat_section}</div>
			) : null}
		</div>
	) : null}
	{!!props.price && (
		<div className="right" style={{ fontSize: 18, marginLeft: 15 }}>
			${addCommas(props.ticket.ticket_cost)}
		</div>
	)}
</>

enum SetPaidType{
	CREATE_PAYMENT = 1,
	ADD_PAYMENT = 2,
	CONVERT_FREE = 3,
}

enum MODAL{
	RESEND = 100,
	SET_PAID = 101,
	EXTEND = 102,

	ORDER_EDIT = 200,
	ORDER_TOTALS = 201,
	ORDER_PAYMENT_EDIT = 202,
	ORDER_DELIVERY_EDIT = 203,
	ORDER_INVOICES = 204,
	ORDER_LOCATION = 205,
	ORDER_EMAILS = 206,

	ORDER_REFUND = 300,
	ORDER_REFUND_REQUEST = 301,
	
	VIEW_PDF = 400,
	CONVERT_FREE = 401,
	CONCILIA = 402,
	MOVE_TICKETS = 403,
	VIEW_RECEIPT = 404,
	// REVIVE_TICKETS = 405,
	DELETE_CONCILIA = 406,

	TICKET_OPTIONS = 500,
	TICKET_DETAILS = 501,
	TICKET_FORM = 502,
	TICKET_USES = 503,
	TICKET_NEW_USE = 504,
	TICKET_EDIT = 505,
	TICKET_USER = 506,
	TICKET_UPDATE_SEAT = 507,
	TICKET_HIDE = 508,
	TICKET_PRINTS = 509,
	TICKET_SELECT = 510,
	TICKET_MOVE_DATE = 511,

	CANCEL_ORDER = 900,
	EXPIRE_ORDER = 901,
	CANCEL_NOT_USED = 902,
	CANCEL_TICKET = 903,

	PAYMENT_INFO = 600,
}

enum SelectTicketType{
	CANCEL_TICKETS = 1,
	PRINT_TICKETS = 2,
	HIDE_TICKETS = 3,
	REVIVE_TICKETS = 4,
}

interface RefundForm{
	commission: boolean,
	delivery: boolean,
	payment: boolean,
	email: string,
	fake?: boolean,
	send_email?: boolean,
}

interface OrderDetails extends Order{
	// payment_cost: number,
}

var OrderView = ()=>{
	var { hash } = useParams();
	var { user, hasAccess, dev } = useUser();
	var { setTitle } = useTitle();
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var navType = useNavigationType();
	var [order, setOrder] = useState<OrderDetails>(null);
	var [loadError, setLoadError] = useState(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [ticket, setTicket] = useState<Ticket>();
	var [ticketForm, setTicketForm] = useState<TicketQuestion[]>(null);
	var [orderPdf, setOrderPdf] = useState<string>(null);
	var [orderEdit, setOrderEdit] = useState<OrderDetails>(null);
	var [confirmCheck, setConfirmCheck] = useState<boolean>(false);
	var [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(null);
	var [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>(null);
	var [ticketUses, setTicketUses] = useState<TicketUse[]>(null);
	var [ticketUseSelected, setTicketUseSelected] = useState<TicketUse>(null);
	var [ticketsSelected, setTicketsSelected] = useState<number[]>([]);
	var [extendAmount, setExtendAmount] = useState<number>(null);
	var [ticketUseDate, setTicketUseDate] = useState<Date>(null);
	var [shownModal, setShownModal] = useState<MODAL>(null);
	var [selectTicketType, setSelectTicketType] = useState<SelectTicketType>(null);
	var [selectedDeposit, setSelectedDeposit] = useState<number>(null);
	var [refundForm, setRefundForm] = useState<RefundForm>(null);
	var [paymentIdSelected, setPaymentIdSelected] = useState<number>(null);
	var [freeComments, setFreeComments] = useState<string>('');
	var [depositForm, setDepositForm] = useState<{ 
		amount: string, 
		payment_id: number,
		type: DepositType, 
		payment_method: number, 
		payment_method_name: string, 
		date_deposit: Date,
		external_id?: string,
	}>(null);
	var SetPaidForm = useValidator({
		type: null,
		payment_id: null,
		payment_method: null,
		gift_description: '',
		tickets: [],
	}, {
		type: [{ rule: 'empty', prompt: 'Favor de seleccionar el pago registrado' }],
		payment_id: [{ rule: (v, d)=>{
			return d.type!==SetPaidType.ADD_PAYMENT || Validator.validate(['number'], v);
		} }],
		tickets: [{ rule: 'minLength', params: [1], label: 'Boletos' }],
		gift_description: [{ rule: (v, d)=>{
			return d.type!==SetPaidType.CONVERT_FREE || Validator.validate([{ rule: 'minLength', params: [3] }], v);
		}, prompt: 'Favor de ingresar una descripción válida' }]
	})

	var MoveOrderForm = useValidator({
		order_hash: '',
		tickets: [],
		moved: false,
	}, {
		order_hash: [{ rule: 'minLength', params: [3], label: 'Orden' }],
		tickets: [{ rule: 'minLength', params: [1], label: 'Boletos' }],
	});

	useEffect(()=>{
		var order_id = null;
		if(hash.startsWith('@')){
			order_id = parseInt(hash.substring(1));
			if(Number.isNaN(order_id)) order_id = null;
		}
		
		if(order){
			if((order_id && order.order_id==order_id) || (hash && order.order_hash==hash.toUpperCase())) return;
			else{
				showModal(null)();
				setOrder(null);
			}
		}

		API.getOrder(order_id ? null : hash, order_id, {
			concilia: true,
			emails: true,
			payments: true,
			refunds: true,
			tickets: true,
			uses: true,
			users: true,
		}, true, navType===NavigationType.Pop).then(res=>{
			if(res.error) return setLoadError(res.message);
			setOrder(res.data);
			setTitle(`Orden ${res.data.order_hash}`);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando las ordenes (LCL-1)');
		});
	}, [hash, order]);

	useEffect(()=>{
		if(!shownModal){
			MoveOrderForm.reset();
		}
		setConfirmCheck(false);
	}, [shownModal]);

	var showTicketOptions = (t?: Ticket)=>{
		return ()=>{
			if(!t) return;
			setModalPrompts(null);
			setTicket(t);
			setShownModal(MODAL.TICKET_OPTIONS)
		}
	}

	var showModal = (modal: MODAL)=>{
		return ()=>{
			setShownModal(modal);
			setConfirmCheck(false);
			setTicketsSelected([]);
			setModalPrompts(null);
		}
	}

	var showTicketForm = ()=>{
		setTicketForm(null);
		setShownModal(MODAL.TICKET_FORM);
		API.getTicketForm(ticket.ticket_id).then(res=>{
			if(res.error){
				setShownModal(null);
				return Toast.error(res.message);
			}
			setTicketForm(res.data);
		}).catch(err=>{
			setShownModal(null);
			return Toast.error('Hubo un error inesperado cargando el formulario del boleto (LCL-1)');
		})
	}

	var showTicketUses = ()=>{
		setTicketUses(null);
		setShownModal(MODAL.TICKET_USES);
		API.getTicketUses(ticket.ticket_id).then(res=>{
			if(res.error){
				setShownModal(null);
				return Toast.error(res.message);
			}
			setTicketUses(res.data);
		}).catch(err=>{
			setShownModal(null);
			return Toast.error('Hubo un error inesperado cargando los usos del boleto (LCL-1)');
		})
	}
	
	var showUseTicket = ()=>{
		setTicketUseDate(getNowLocal().toJSDate());
		setShownModal(MODAL.TICKET_NEW_USE);
	}

	var resend = (setLoading: SetLoading)=>{
		if(order.cancelled) return Toast.error('No se puede reenviar el correo de una orden cancelada.');
		setLoading(true);
		API.resendEmail(order.order_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se ha reenviado el correo al cliente.');
			setShownModal(null);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado reenviando el correo. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showSetPaid = (tickets: number[])=>{
		return ()=>{
			SetPaidForm.reset({ tickets });
			setShownModal(MODAL.SET_PAID);
		}
	}

	var setPaid = (setLoading: SetLoading)=>{
		var { valid } = SetPaidForm.validate();
		if(!valid) return;

		setLoading(true);
		var { data } = SetPaidForm;
		var payment_id = data.type===SetPaidType.ADD_PAYMENT ? data.payment_id : null;
		API.createTicketsPayment(order.order_id, data.tickets, {
			payment_method: data.type===SetPaidType.ADD_PAYMENT ? null : (data.type===SetPaidType.CONVERT_FREE ? PaymentMethods.FREE : data.payment_method),
			payment_id,
			gift_description: data.type===SetPaidType.CONVERT_FREE ? data.gift_description : null,
			delivery_cost: null,
		}).then(res=>{
			if(res.error) return SetPaidForm.setPrompts([res.message]);
			Toast.success('Se han marcado los boletos como pagados');
			var ord = {...order};
			var now = getNow().toSeconds();
			for(var i of ord.tickets){
				if(res.data.tickets.indexOf(i.ticket_id)!=-1){
					i.paid = true;
					i.date_paid = now;
					i.payment_id = res.data.payment_id;
					if(data.type===SetPaidType.CONVERT_FREE){
						i.commission = 0;
						i.ticket_cost = 0;
					}
				}
			}
			if(!ord.payments) ord.payments = [];
			if(ord.payments.findIndex(a=>a.payment_id===res.data.payment_id)==-1){
				ord.payments.push({
					date_created: now,
					date_executed: now,
					date_expiration: null,
					date_paid: now,
					delivery_cost: 0,
					executed: true,
					external_id: null,
					order_id: order.order_id,
					paid: true,
					payment_cost: 0,
					payment_id: res.data.payment_id,
					payment_method: res.data.payment_method,
					pdv_id: null,
					reference: null,
					total: res.data.total,
					total_available: res.data.total,
					total_refunded: 0,
					user_creator: user.user_id,
				});
			}else if(payment_id){
				var pix = ord.payments.findIndex(a=>a.payment_id===payment_id);
				if(pix!==-1){
					ord.payments[pix].paid = true;
					ord.payments[pix].date_paid = now;
					ord.payments[pix].user_creator = user.user_id;
				}
			}
			setOrder(ord);
			setShownModal(null);
		}).catch(err=>{
			SetPaidForm.setPrompts([t('common.unexpected_code', { code: 'LCL-ORVWSTPD-1' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendExtend = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.ORDERS.EXTEND_EXPIRATION)) return;
		if(!extendAmount && extendAmount<=0) return setModalPrompts(['Favor de seleccionar el tiempo a agregar']);
		setLoading(true);
		API.extendOrder(order.order_id, extendAmount).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var tickets = [...order.tickets];
			for(var i of res.data.tickets){
				var tx = tickets.findIndex(a=>a.ticket_id===i.ticket_id);
				if(tx!=-1){
					tickets[tx].paid = i.paid;
					tickets[tx].cancelled = i.cancelled;
					tickets[tx].date_expiration = i.date_expiration;
				}
			}
			setOrder(ord=>({
				...ord,
				tickets
			}));
			setShownModal(null);
			Toast.success('Se ha extendido el tiempo de pago de los boletos');
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado extendiendo la orden. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var viewOrderPdf = ()=>{
		setShownModal(MODAL.VIEW_PDF);
		if(!orderPdf){
			API.getOrderPdf(order.order_id).then(res=>{
				if(res.error || !res.data || !res.data.url){
					Toast.error(res.message || 'Hubo un error inesperado cargando el PDF de la orden (LCL-2)');
					return setShownModal(null);
				}
				setOrderPdf(res.data.url);
			}).catch(err=>{
				Toast.error('Hubo un error inesperado cargando el PDF de la orden (LCL-1)');
				return setShownModal(null);
			});
		}
	}

	var showOrderEdit = ()=>{
		setModalPrompts(null);
		setOrderEdit({...order});
		setShownModal(MODAL.ORDER_EDIT);
	}

	var sendOrderEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(orderEdit, {
			first_name: [{
				rule: 'length', params: [3, 32], label: 'Nombre'
			}],
			last_name: [{
				rule: 'length', params: [3, 32], label: 'Apellido'
			}],
			email: [{
				rule: 'email', label: 'Correo electrónico'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editOrder(order.order_id, {
			first_name: orderEdit.first_name, 
			last_name: orderEdit.last_name, 
			email: orderEdit.email,
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ord = {...order};
			ord.email = orderEdit.email;
			ord.first_name = orderEdit.first_name;
			ord.last_name = orderEdit.last_name;
			setOrder(ord);
			setShownModal(null);
			setOrderEdit(null);
			Toast.success('Se ha editado la orden.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado editando la orden (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showTotalsEdit = ()=>{
		setModalPrompts(null);
		setOrderEdit({...order});
		setShownModal(MODAL.ORDER_TOTALS);
	}

	var sendTotalsEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(orderEdit, {
			delivery_cost: [{
				rule: 'float', label: 'Entrega'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editOrder(order.order_id, {
			delivery_cost: orderEdit.delivery_cost,
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ord = {...order};
			ord.delivery_cost = parseFloat(orderEdit.delivery_cost.toString());
			setOrder(ord);
			setShownModal(null);
			setOrderEdit(null);
			Toast.success('Se ha editado la orden.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado los costos de la orden (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var getPaymentMethods = ()=>{
		if(!paymentMethods){
			API.getEventsQL(['payment_methods']).then(res=>{
				if(res.error){
					Toast.error(res.message);
					return setShownModal(null);
				}
				setPaymentMethods(res.data.payment_methods);
			}).catch(err=>{
				setShownModal(null);
				return Toast.error('Hubo un error inesperado cargando los métodos de pago. (LCL-1)');
			})
		}
	}

	var showPaymentMethodChange = ()=>{
		setOrderEdit({...order});
		setShownModal(MODAL.ORDER_PAYMENT_EDIT);
		getPaymentMethods();
	}

	var sendPaymentChange = (setLoading: SetLoading)=>{
		if(order.payment_method===orderEdit.payment_method){
			return Toast.success('El método de pago no ha cambiado.');
		}
		var { valid, prompts } = Validator(orderEdit, {
			payment_method: [{
				rule: 'number', label: 'Forma de pago'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editOrder(order.order_id, {
			payment_method: orderEdit.payment_method
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ord = {...order};
			ord.payment_method = parseFloat(orderEdit.payment_method.toString());
			if(paymentMethods){
				try{
					var pm = paymentMethods.find(a=>a.method_id==orderEdit.payment_method);
					if(pm){
						ord.payment_method_name = pm.method_name;
						ord.payment_method_name_internal = pm.method_name_internal;
					}
				}catch(e){}
			}
			setOrder(ord);
			setShownModal(null);
			setOrderEdit(null);
			Toast.success('Se ha editado la orden.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado los costos de la orden (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showDeliveryChange = ()=>{
		setOrderEdit({...order});
		setShownModal(MODAL.ORDER_DELIVERY_EDIT);
		if(!deliveryMethods){
			API.getEventsQL(['delivery_methods']).then(res=>{
				if(res.error){
					Toast.error(res.message);
					return setShownModal(null);
				}
				setDeliveryMethods(res.data.delivery_methods);
			}).catch(err=>{
				setShownModal(null);
				return Toast.error('Hubo un error inesperado cargando los formas de entrega. (LCL-1)');
			})
		}
	}

	var sendDeliveryChange = (setLoading: SetLoading)=>{
		if(order.delivery_id===orderEdit.delivery_id){
			return Toast.success('La forma de entrega no ha cambiado.');
		}
		var { valid, prompts } = Validator(orderEdit, {
			delivery_id: [{
				rule: 'number', label: 'Entrega'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editOrder(order.order_id, {
			delivery_id: orderEdit.delivery_id
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ord = {...order};
			ord.delivery_id = parseFloat(orderEdit.delivery_id.toString());
			if(deliveryMethods){
				try{
					var dm = deliveryMethods.find(a=>a.delivery_id==orderEdit.delivery_id);
					if(dm){
						ord.delivery_name = dm.method_name;
						ord.delivery_needs_location = dm.needs_location;
					}
				}catch(e){}
			}
			setOrder(ord);
			setShownModal(null);
			setOrderEdit(null);
			Toast.success('Se ha editado la orden.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado los costos de la orden (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendConvertFree = (setLoading: SetLoading)=>{
		if(!confirmCheck) return;
		var { valid, prompts } = Validator({ freeComments }, {
			freeComments: [{
				rule: 'minLength',
				params: [3],
				prompt: 'Favor de ingresar la descripción de la cortesía'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.convertFree(order.order_id, freeComments).then(res=>{
			if(res.error) return Toast.error(res.message);
			var ord = {...order};
			ord.total_tickets = 0;
			ord.delivery_cost = 0;
			ord.commission = 0;
			if(ord.tickets){
				for(var i of ord.tickets){
					i.ticket_cost = 0;
					i.commission = 0;
					i.gift_id = 1;
				}
			}
			if(!ord.payments) ord.payments = [];
			for(var p of ord.payments){
				p.cancelled = true;
			}
			setOrder(ord);
			Toast.success('Se ha convertido la orden en cortesía');
			setShownModal(null);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado convirtiendo en cortesía. (LCL-1)')
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendCancelTickets = (tickets: number[], cancel_reason: CancelReason, setLoading?: SetLoading)=>{
		if(setLoading) setLoading(true);
		API.cancelTicket(tickets, cancel_reason).then(res=>{
			if(res.error) return Toast.error(res.message);
			var ord = {...order};
			if(ord.tickets){
				for(var t of res.data.tickets){
					var tx = ord.tickets.findIndex(a=>a.ticket_id===t);
					if(tx==-1) continue;
					ord.tickets[tx].cancelled = true;
					ord.tickets[tx].date_cancelled = getNowLocal().toSeconds();
					ord.tickets[tx].user_cancelled = user.user_id;
					ord.tickets[tx].user_cancelled_name = user.name;
				}
			}
			if(ord.payments){
				for(let i of res.data.payments){
					var px = ord.payments.findIndex(a=>a.payment_id===i.payment_id);
					if(px==-1) continue;
					if(!ord.payments[px].total_refunded) ord.payments[px].total_refunded = 0;
					ord.payments[px].total_refunded += i.total_refunded;
					ord.payments[px].total_available -= i.total_refunded;
				}
			}
			if(!ord.refunds) ord.refunds = [];
			for(let r of res.data.refunds){
				ord.refunds.push({
					refund_id: r.refund_id,
					refund_type: r.refund_type,
					cancelled: false,
					completed: true,
					date_cancelled: null,
					date_completed: getNow().toSeconds(),
					date_created: getNow().toSeconds(),
					delivery_cost: 0,
					executed: true,
					order_hash: order.order_hash,
					order_id: order.order_id,
					payment_cost: 0,
					total: r.amount,
					total_tickets: r.amount,
				})
			}

			setOrder(ord);
			setShownModal(null);
			if(tickets.length==0){
				Toast.success(`Se ha cancelado el boleto ${ticket[0]}`);
			}else{
				Toast.success(`Se han cancelado los boletos seleccionados`);
			}
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'LCL-ORDVW-CCLTK-1' }));
		}).finally(()=>{
			if(setLoading) setLoading(false);
		})
	}

	var cancelSingleTicket = (setLoading: SetLoading)=>{
		if(!confirmCheck || !ticket) return;
		sendCancelTickets([ticket.ticket_id], CancelReason.GENERIC, setLoading);
	}

	var cancelMultipleTickets = (setLoading: SetLoading)=>{
		if(!confirmCheck || !ticketsSelected || ticketsSelected.length==0) return;
		sendCancelTickets(ticketsSelected, CancelReason.GENERIC, setLoading);
	}
	
	var sendCancelOrder = (setLoading: SetLoading)=>{
		if(!confirmCheck) return;
		setLoading(true);
		API.cancelOrder(order.order_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			var ord = {...order};
			ord.cancelled = true;
			ord.delivery_cost = 0;
			ord.date_cancelled = getNow().toLocal().toSeconds();
			if(ord.tickets){
				for(let i of ord.tickets){
					i.cancelled = true;
				}
			}
			if(ord.payments){
				for(let i of ord.payments){
					i.cancelled = true;
				}
			}
			setOrder(ord);
			setShownModal(null);
			Toast.success('Se ha cancelado la orden.');
		}).catch(err=>{
			return Toast.error('Hubo un error inesperado cancelando la orden. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendCancelNotUsed = (setLoading: SetLoading)=>{
		if(!confirmCheck) return;
		setLoading(true);
		setModalPrompts(null);
		API.cancelNotUsedTickets(order.order_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var tk = order.tickets ? [...order.tickets] : [];
			var pys = order.payments ? [...order.payments] : [];
			var now = getNow().toSeconds();
			if(order.tickets){
				for(var i of res.data.tickets){
					var tx = tk.findIndex(a=>a.ticket_id===i);
					if(tx===-1) continue;
					tk[tx].cancelled = true;
					tk[tx].date_cancelled = now;
					tk[tx].user_cancelled = user.user_id;
					tk[tx].user_cancelled_name = user.name;
				}
			}
			if(order.payments){
				for(var r of res.data.refunds){
					var px = pys.findIndex(a=>a.payment_id===r.payment_id);
					if(px===-1) continue;
					pys[px].total_refunded += r.amount;
					pys[px].total_available -= r.amount;
				}
			}
			
			setOrder({
				...order,
				tickets: tk,
				payments: pys
			});
			setShownModal(null);
			Toast.success(`Se ha${res.data.tickets.length==1 ? '' : 'n'} cancelado ${res.data.tickets.length} boleto${res.data.tickets.length==1 ? '' : 's'} no usado${res.data.tickets.length==1 ? '' : 's'} de la orden`);
		}).catch(err=>{
			console.log(err);
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-CLORNTU-1' })])
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendExpireOrder = (setLoading: SetLoading)=>{
		if(!confirmCheck) return;
		setLoading(true);
		API.expireOrder(order.order_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setShownModal(null);
			if(res.data.tickets.length==0){
				return Toast.success('Esta orden no tiene boletos pendientes de pago expirables');
			}
			var now = moment().unix();
			var tks = [...order.tickets];
			for(var i of res.data.tickets){
				var tx = tks.findIndex(a=>a.ticket_id===i);
				if(tks[tx].paid || tks[tx].cancelled) continue;
				tks[tx].date_expiration = now-3600;
			}

			setOrder({
				...order,
				tickets: tks
			});
			Toast.success('Se han expirado los boletos pendientes de pago');
		}).catch(err=>{
			return Toast.error('Hubo un error inesperado expirando la orden. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendReviveTickets = (setLoading: SetLoading)=>{
		if(ticketsSelected.length==0) return;
		setLoading(true);
		API.reviveTickets(order.order_id, ticketsSelected).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(!res.data || res.data.length==0) return setModalPrompts(['No se pudo revivir ninguno de los boletos. (LCL-2)']);
			var ord = {...order};
			for(var i of res.data){
				var tk = ord.tickets.findIndex(a=>a.ticket_id==i);
				if(tk==-1) continue;
				ord.tickets[tk].cancelled = false;
			}
			ord.cancelled = false;
			setOrder(ord);
			setShownModal(null);
			Toast.success('Se han revivido los boletos seleccionados.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado reviviendo los boletos. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendMoveTickets = (setLoading: SetLoading)=>{
		var { valid } = MoveOrderForm.validate();
		if(!valid) return;
		setLoading(true);
		API.moveTickets(MoveOrderForm.data.tickets, MoveOrderForm.data.order_hash).then(res=>{
			if(res.error) return MoveOrderForm.setPrompts([res.message]);

			var ord = {...order};
			ord.tickets = ord.tickets.filter(a=>MoveOrderForm.data.tickets.indexOf(a.ticket_id)==-1);
			setOrder(ord);

			MoveOrderForm.setValue('moved', true);
			Toast.success('Se han movido los boletos a la orden ingresada');
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-OVWSMVTK' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendFormEdit = (setLoading: SetLoading)=>{
		var data : TicketFormPayload[] = ticketForm.map(a=>{
			var data : TicketFormPayload = { question_id: a.question_id, required: a.required }
			if(a.question_type==QuestionType.FILE_IMAGE || a.question_type==QuestionType.FILE_OTHER){
				if(a.files){
					data.files = a.files;
				}else data.files = null;
			}else if(a.question_type==QuestionType.MULTIPLE || a.question_type==QuestionType.SELECTION){
				data.selected = a.selected && a.selected.length>0 ? a.selected : (a.value_id ? [a.value_id] : []);
			}else{
				data.answer = a.answer;
			}
			return data;
		});

		for(var d of data){
			if(d.required && ((!d.answer || d.answer.length==0) && (!d.files || d.files.length==0) && d.selected.length==0)){
				return setModalPrompts(['Uno o mas preguntas requeridas del formulario están faltantes.']);
			}
		}

		setModalPrompts(null);
		var formdata = new FormData();
		for(var i=0; i<data.length; i++){
			var a = data[i];
			if((a.files && a.files.length>0) || (a.selected && a.selected.length>0) || a.answer){
				formdata.append(`answers[${i}][question_id]`, a.question_id.toString());
			}else continue;
			if(a.files){
				a.files.forEach((v, ix)=>{
					formdata.append(`answers[${i}][files][${ix}]`, v);
				})
			}else if(a.selected && a.selected.length>0){
				a.selected.forEach((v, ix)=>{
					formdata.append(`answers[${i}][selected][${ix}]`, v.toString());
				});
			}else if(a.answer){
				formdata.append(`answers[${i}][answer]`, a.answer);
			}
		}
		setLoading(true);
		API.editTicketForm(order.order_id, ticket.ticket_id, formdata).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha guardado el formulario.');
			showTicketForm()
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado guardando el formulario (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendTicketEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(ticket, {
			ticket_cost: [
				{ rule: 'float', prompt: 'El precio del boleto no es válido.' },
			],
			commission: [
				{ rule: 'float', prompt: 'El precio del boleto no es válido.' },
			]
		})
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editTicket(ticket.ticket_id, ticket.ticket_cost, ticket.commission).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha guardado el precio del boleto.');
			setShownModal(null);
			var ord = {...order};
			var tx = ord.tickets.findIndex(a=>a.ticket_id==ticket.ticket_id);
			if(tx==-1) return;
			ord.tickets[tx].ticket_cost = parseFloat(ticket.ticket_cost.toString());
			ord.tickets[tx].commission = parseFloat(ticket.commission.toString());
			setOrder(ord);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado editando el precio del boleto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendUseTicket = (setLoading: SetLoading)=>{
		if(!ticket) return;
		if(ticketUseDate) return setModalPrompts(['La fecha de uso no es válida.']);
		setLoading(true);
		API.ticketUse(ticket.ticket_id, Math.floor(ticketUseDate.getTime()/1000)).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha usado el boleto.');
			setShownModal(null);
			var ord = {...order};
			var tx = ord.tickets.findIndex(a=>a.ticket_id==ticket.ticket_id);
			if(tx!=-1) ord.tickets[tx].uses = ord.tickets[tx].uses ? ord.tickets[tx].uses+1 : 1;
			setOrder(ord);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado usando el boleto. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var deleteTicketUse = (setLoading: SetLoading)=>{
		if(!ticketUseSelected) return;
		setLoading(true);
		API.deleteTicketUse(ticket.ticket_id, ticketUseSelected.use_id).then(res=>{
			if(res.error) return setModalPrompts([res.message])
			var ord = {...order};
			var tx = ord.tickets.findIndex(a=>a.ticket_id==ticket.ticket_id);
			if(tx!=-1) ord.tickets[tx].uses = ord.tickets[tx].uses ? ord.tickets[tx].uses-1 : 1;
			setOrder(ord);
			showTicketUses();
			setTicketUseSelected(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el uso del boleto. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showDeleteDeposit = (deposit_id: number)=>{
		return ()=>{
			if(!hasAccess(UserAccess.CONTABILIDAD.CANCEL_DEPOSIT)){
				return Toast.error('No tienes acceso a realizar esta acción')
			}
			setSelectedDeposit(deposit_id);
			showModal(MODAL.DELETE_CONCILIA)();
		}
	}

	var showCreateDeposit = ()=>{
		var pm = order.payments.find(a=>a.paid && !a.cancelled);
		var now = getNowLocal();
		now.set({
			hour: 12,
			minute: 0,
			second: 0
		})
		setDepositForm({ 
			amount: null,
			type: null,
			payment_id: null,
			payment_method: pm?.payment_method,
			payment_method_name: pm?.method_name_internal,
			date_deposit: now.toJSDate(),
			external_id: ''
		});
		showModal(MODAL.CONCILIA)();
		getPaymentMethods();
	}

	var showSelectTickets = (type: SelectTicketType)=>{
		return ()=>{
			setSelectTicketType(type);
			setShownModal(MODAL.TICKET_SELECT);
			setTicketsSelected([]);
		}
	}

	var deleteDeposit = (setLoading: SetLoading)=>{
		if(!selectedDeposit) return;
		setLoading(true);
		setModalPrompts(null);
		API.deleteDeposit(selectedDeposit).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(order && order.concilia){
				var ord = {...order};
				ord.concilia = ord.concilia.filter(a=>a.deposit_id!=selectedDeposit);
				setOrder(ord);
			}
			setSelectedDeposit(null);
			setShownModal(null);
			Toast.success('Se ha eliminado la conciliación seleccionada.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado cancelando la conciliación (LCL-1)']);
		})
	}

	var onSaveNewTicketSeat = (seat: SeatmapSeat, date: EventDateData, section: EventDateData["sections"][0], price: EventDateData["sections"][0]["prices"][0], setLoading: SetLoading)=>{
		if(!ticket) return;
		setLoading(true);
		API.updateTicketSeat(ticket.ticket_id, seat.seat_id, (price?.price_id || ticket.price_id)).then(res=>{
			if(res.error) return Toast.error(res.message);
			setTicket(null);
			var newOrder = {...order};
			newOrder.tickets = order.tickets.map(t => {
				if(t.ticket_id === ticket.ticket_id) {
					t.seat_id = seat.seat_id;
					t.seat_number = seat.seat_number;
					t.seat_row = seat.seat_row;
					t.seat_section = seat.seat_section;
					if(date){
						t.date_id = date.date_id;
						t.date = date.date;
					}
					if(section){
						t.section_id = section.section_id;
						t.section_name = section.section_name;
					}
					if(price){
						t.price_id = price.price_id;
						t.price_name = price.price_name;
					}
				}
				return t;
			});
			setOrder(newOrder);
			Toast.success('Se ha cambiado la butaca del boleto');
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'LCL-ORVW-SNTKST-1' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onTicketPriceUpdate = (ticket_id: number, date: EventDateData, section: EventDateData["sections"][0], price: EventDateData["sections"][0]["prices"][0])=>{
		setTicket(null);
		var newOrder = {...order};
		newOrder.tickets = order.tickets.map(t => {
			if(t.ticket_id === ticket.ticket_id) {
				t.seat_id = null;
				t.seat_number = null;
				t.seat_row = null;
				t.seat_section = null;
				if(date){
					t.date_id = date.date_id;
					t.date = date.date;
				}
				if(section){
					t.section_id = section.section_id;
					t.section_name = section.section_name;
				}
				if(price){
					t.price_id = price.price_id;
					t.price_name = price.price_name;
				}
			}
			return t;
		});
		setOrder(newOrder);
		Toast.success('Se ha cambiado la fecha del boleto');
	}

	var createDeposit = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.CONTABILIDAD.ADD_DEPOSIT)) return;
		var { valid, prompts } = Validator(depositForm || {}, {
			amount: [{
				rule: 'min', params: [1], prompt: 'La cantidad del deposito es inválida',
			}],
			payment_id: [{
				rule: 'empty', prompt: 'Favor de seleccionar el método de pago',
			}],
			type: [{
				rule: 'empty', prompt: 'Favor de seleccionar el tipo de deposito',
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.createDeposit(depositForm.payment_id, parseFloat(depositForm.amount), Math.floor(depositForm.date_deposit.getTime()/1000), depositForm.type, depositForm.external_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(!res.data.deposit_id) return setModalPrompts(['Hubo un error inesperado creando la conciliación (LCL-2)']);
			var ord = {...order};
			if(!ord.concilia) ord.concilia = [];
			ord.concilia.push({
				deposit_id: res.data.deposit_id,
				payment_id: depositForm.payment_id,
				user_creator: user.user_id,
				amount: parseFloat(depositForm.amount.toString()) * (depositForm.type===DepositType.DEPOSIT ? 1 : -1),
				external_id: depositForm.external_id,
				cancelled: false,
				date_cancelled: null,
				date_created: moment().unix(),
				date_deposit: Math.floor(depositForm.date_deposit.getTime()/1000),
				deposit_type: depositForm.type,
				deposit_type_name: depositForm.type===DepositType.DEPOSIT ? 'Deposito' : (depositForm.type===DepositType.REFUND ? 'Reembolso' : 'Contracargo'),
				payment_method: depositForm.payment_method,
				payment_method_name: depositForm.payment_method_name,
			});
			setOrder(ord);
			setShownModal(null);
			Toast.success('Se ha agregado la conciliación.');
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado creando la conciliación (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showRefundForm = ()=>{
		setRefundForm({
			commission: false,
			delivery: false,
			email: order.email,
			payment: false,
			send_email: true,
			fake: false,
		});
		showModal(MODAL.ORDER_REFUND)();
	}

	var createRefund = (setLoading: SetLoading)=>{
		if(!user_access.refund) return;
		if((refundForm.commission || refundForm.delivery || refundForm.payment) && !user_access.refund_complete) return;
		if(refundForm.fake && !user_access.refund_fake) return;

		if(!order.email || order.email.length<5){
			if(!Validator.validate([{ rule: 'email' }], refundForm.email)){
				return setModalPrompts(['El correo electrónico no es válido.']);
			}
		}

		setLoading(true);
		API.createRefund(order.order_id, ticketsSelected, {
			commission: refundForm.commission,
			delivery: refundForm.delivery,
			email: refundForm.email,
			payment: refundForm.payment,
			completed: refundForm.fake,
			send_email: refundForm.send_email,
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(!res.data || !res.data?.refund) return setModalPrompts(['Hubo un error inesperado creando el reembolso (LCL-2)']);
			var refunds = order.refunds ? [...order.refunds] : [];
			for(var i of refunds){
				if(!i.executed) i.cancelled = true;
			}

			var payments = order.payments ? [...order.payments] : [];
			if(res.data.payments){
				for(var p of res.data.payments){
					var px = payments.findIndex(a=>a.payment_id==p.payment_id);
					if(px==-1) continue;
					payments[px].total_refunded += p.amount;
				}
			}

			var tickets = order.tickets ? [...order.tickets] : [];
			if(res.data.tickets){
				for(var t of res.data.tickets){
					var tx = tickets.findIndex(a=>a.ticket_id==t.ticket_id);
					if(tx===-1) continue;
					tickets[tx].cancelled = true;
					tickets[tx].refunded = 1;
					tickets[tx].amount_refunded = t.amount_refunded;
					tickets[tx].commission_refunded = t.commission_refunded;
					tickets[tx].cancel_reason = CancelReason.REFUNDED;
				}
			}

			setOrder({
				...order,
				delivery_cost: order.delivery_cost-(!refundForm.fake ? 0 : res.data.refund.delivery_cost),
				tickets,
				payments,
				refunds: [
					...refunds,
					res.data.refund,
				]
			});
			showModal(null)();
			if(refundForm.fake){
				Toast.success('Se ha marcado los boletos como reembolsados.')
			}else{
				Toast.success('Se le ha enviado a el cliente la solicitud de reembolso.')
			}
		}).catch(err=>{
			return setModalPrompts(['Hubo un error inesperado creando el reembolso (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onOpenPaymentModal = (payment_id: number) => {
		setPaymentIdSelected(payment_id)
		setShownModal(MODAL.PAYMENT_INFO);
	}

	var hideMultipleTickets = (tickets: number[], hiding: boolean, setLoading: SetLoading)=>{
		setLoading(true);
		API.setTicketHidden(tickets, hiding).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se han escondido los boletos seleccionados')
			var tk = [...order.tickets];
			for(var t of tickets){
				var tx = tk.findIndex(a=>a.ticket_id===t);
				if(tx===-1) continue;
				tk[tx].hidden = !!hiding;
			}
			setOrder({
				...order,
				tickets: tk,
			});
			setShownModal(null);
		}).catch(err => {
			Toast.error(t('common.unexpected_code', { code: 'LCL-ORVW-HMULTK-1' }));
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onHideTicket = (setLoading: SetLoading) => {
		
	}

	var onPaymentChange = (paymentInfo: { payment_id: number, payment_cost: number, delivery_cost: number, total: number }) => {
		var pays = [...order.payments];
		var ix = pays.findIndex(a=>a.payment_id===paymentInfo.payment_id);
		if(ix===-1) return;
		pays[ix].payment_cost = paymentInfo.payment_cost;
		pays[ix].delivery_cost = paymentInfo.delivery_cost;
		pays[ix].total = paymentInfo.total;
		pays[ix].total_available = paymentInfo.total-pays[ix].total_refunded;

		setOrder({
			...order,
			payments: pays
		});
	}

	var onPaymentPaid = (payment_id: number, tickets: number[])=>{
		var pays = [...order.payments];
		var ix = pays.findIndex(a=>a.payment_id===payment_id);
		if(ix===-1) return;
		var now = moment().unix();
		pays[ix].paid = true;
		pays[ix].date_paid = now;
		pays[ix].cancelled = false;

		var tks = [...order.tickets];
		if(tickets && tickets.length>0){
			for(var i of tickets){
				var tx = tks.findIndex(a=>a.ticket_id==i);
				if(tx==-1) continue;
				tks[tx].paid = true;
				tks[tx].cancelled = false;
				tks[tx].date_paid = now;
			}
		}

		setOrder({
			...order,
			tickets: tks,
			payments: pays
		});
		Toast.success(`Se ha marcado el pago y ${(tickets?.length || 0)} boleto${(tickets?.length || 0)==1 ? '' : 's'} como pagado`);
	}

	var onLocationChange = (location: Location) => {
		if (location.location_id !== order.delivery_location) {
			API.updateOrderLocation(order.order_id, location.location_id).then(res => {
				if (res.error) return Toast.error('Ocurrio un error actualizando la direccion');
				setOrder({
					...order,
					delivery_location: location.location_id
				});
				return Toast.success('Se actualizo la direccion de la orden correctamente');
			}).catch(err => {
				Toast.error('Hubo un error inesperado (LCL-1)');
			});
		}
	}

	var user_access = useMemo(()=>{
		return {
			VIEW_ORDERS: hasAccess(UserAccess.ORDERS.VIEW_ORDERS),
			tickets: hasAccess(UserAccess.ORDERS.VIEW_TICKETS),
			VIEW_ORDER_EMAILS: hasAccess(UserAccess.ORDERS.VIEW_ORDER_EMAILS),
			VIEW_PDF: hasAccess(UserAccess.ORDERS.VIEW_PDF),
			CHANGE_ORDER_DATA: hasAccess(UserAccess.ORDERS.CHANGE_ORDER_DATA),
			CHANGE_TOTALS: hasAccess(UserAccess.ORDERS.CHANGE_TOTALS),
			CHANGE_DELIVERY_METHOD: hasAccess(UserAccess.ORDERS.CHANGE_DELIVERY_METHOD),
			payments: hasAccess(UserAccess.ORDERS.VIEW_PAYMENTS),
			extend: hasAccess(UserAccess.ORDERS.EXTEND_EXPIRATION),
			REVIVE_TICKET: hasAccess(UserAccess.ORDERS.REVIVE_TICKET),
			concilia: hasAccess(UserAccess.CONTABILIDAD.VIEW_DEPOSITS),
			view_refund: hasAccess(UserAccess.ORDERS.VIEW_REFUND),
			refund_request: hasAccess(UserAccess.ORDERS.CREATE_REFUND_REQUEST),
			refund: hasAccess(UserAccess.ORDERS.CREATE_REFUND),
			refund_complete: hasAccess(UserAccess.ORDERS.REFUND_COMPLETE),
			refund_fake: hasAccess(UserAccess.ORDERS.CREATE_FAKE_REFUND),
			SET_TICKET_PAID: hasAccess(UserAccess.ORDERS.SET_TICKET_PAID),
			VIEW_TICKET_FORM: hasAccess(UserAccess.ORDERS.VIEW_TICKET_FORM),
			VIEW_TICKET_USES: hasAccess(UserAccess.ORDERS.VIEW_TICKET_USES),
			CANCEL_TICKET_PRINT: hasAccess(UserAccess.ORDERS.CANCEL_TICKET_PRINT),
			USE_TICKET: hasAccess(UserAccess.ORDERS.USE_TICKET),
			EDIT_TICKET_PRICE: hasAccess(UserAccess.ORDERS.EDIT_TICKET_PRICE),
			HIDE_ORDERS: hasAccess(UserAccess.ORDERS.HIDE_ORDERS),
			CANCEL_TICKET: hasAccess(UserAccess.ORDERS.CANCEL_TICKET),
			CANCEL_UNUSED: hasAccess(UserAccess.ORDERS.CANCEL_UNUSED),
			CREATE_FREE: hasAccess(UserAccess.ORDERS.CREATE_FREE),
			MANUAL_DEPOSIT: hasAccess(UserAccess.ORDERS.MANUAL_DEPOSIT),
			MOVE_TICKETS: hasAccess(UserAccess.ORDERS.MOVE_TICKETS),
			CHANGE_TICKET_SEAT: hasAccess(UserAccess.ORDERS.CHANGE_TICKET_SEAT),
			DELETE_TICKET_USE: hasAccess(UserAccess.ORDERS.DELETE_TICKET_USE),
			EDIT_PAYMENT_AMOUNTS: hasAccess(UserAccess.ORDERS.DELETE_TICKET_USE),
			CHANGE_ORDER_EMAIL: hasAccess(UserAccess.ORDERS.CHANGE_ORDER_EMAIL),
		}
	}, []);

	var now = useMemo(()=>{
		return moment().unix();
	}, []);

	var { total_tickets, total_commission, tickets_paid, tickets_free, tickets_cancelled, tickets_refunded, last_ticket_paid_date, order_expired, next_ticket_expiration, tickets_pending, total_tickets_pending } = useMemo(()=>{
		var total_tickets = 0, total_commission = 0;
		var tickets_paid = 0, tickets_cancelled = 0, tickets_refunded = 0, last_ticket_paid_date = -1;
		var tickets_free = 0, tickets_pending = 0;
		var total_tickets_pending = 0;
		var order_expired = false, next_ticket_expiration = null;
		var now = moment().unix();
		if(order?.tickets){
			order_expired = true;
			for(var i of order.tickets){
				if(i.cancelled){
					if(i.cancel_reason===CancelReason.REFUNDED) tickets_refunded += 1;
					else tickets_cancelled += 1;
					if(i.cancel_reason!==CancelReason.EXPIRED) order_expired = false;
					continue;
				}
				if(!i.paid && i.date_expiration && i.date_expiration<=now) continue;
				if(!i.date_expiration || i.date_expiration>now || !!i.paid){
					order_expired = false;
				}
				if(!i.paid && !i.cancelled && !!i.date_expiration){
					next_ticket_expiration = i.date_expiration;
				}
				var is_expired = !!i.date_expiration && i.date_expiration<now;
				if(!i.paid && !i.cancelled && !is_expired){
					tickets_pending += 1;
					total_tickets_pending += i.ticket_cost+i.commission;
				}
				if(!!i.paid){
					if(!!i.gift_id) tickets_free += 1;
					tickets_paid += 1;
					last_ticket_paid_date = Math.max(i.date_paid, last_ticket_paid_date)
				}
				total_tickets += i.ticket_cost-(i.amount_refunded-i.commission_refunded);
				total_commission += i.commission-(i.commission_refunded || 0);
			}
		}
		return { total_tickets, total_commission, tickets_paid, tickets_cancelled, tickets_refunded, last_ticket_paid_date, order_expired, next_ticket_expiration, tickets_free, tickets_pending, total_tickets_pending };
	}, [order, order?.tickets])

	var active_payments = useMemo(()=>{
		return order?.payments ? order.payments.filter(a=>a.paid && !a.cancelled) : [];
	}, [order?.payments]);

	var { tickets_alive, tickets_alive_pending } = useMemo(()=>{
		var tickets_alive : (typeof order.tickets) = [];
		var tickets_alive_pending : (typeof order.tickets) = [];

		if(!!order && !!order.tickets){
			for(var i of order.tickets){
				if(!i.cancelled){
					if(i.paid) tickets_alive.push(i);
					else if(!i.date_expiration || i.date_expiration>now){
						tickets_alive.push(i);
						tickets_alive_pending.push(i);
					}
				}
			}
		}

		return { tickets_alive, tickets_alive_pending } 
	}, [order?.tickets, now]);

	var { payments_total, total_refunded, total_final, total_paid, total_payment_cost, paid_payments } = useMemo(()=>{
		var payments_total = 0, total_refunded = 0, total_final = 0, total_paid = 0, total_payment_cost = 0;
		var paid_payments = 0;
		if(order && order.payments){
			for(var p of order.payments){
				if(!p.paid || p.cancelled) continue;
				payments_total += typeof p.total_available !== 'undefined' ? p.total_available : p.total;
				total_refunded += typeof p.total_available !== 'undefined' ? p.total_refunded : 0;
				paid_payments++;
				total_paid += p.total;
				total_payment_cost = p.payment_cost;
			}
			total_final = total_tickets+total_commission+total_payment_cost+order.delivery_cost;
		}
		return { payments_total, total_refunded, total_final, total_paid, total_payment_cost, paid_payments };
	}, [order])

	var { total_concilia_deposits, total_concilia_refunds } = useMemo(()=>{
		var total_concilia_deposits = 0, total_concilia_refunds = 0;
		if(order?.concilia){
			for(var c of order.concilia){
				if(c.amount>0){
					total_concilia_deposits += c.amount;
				}else{
					total_concilia_refunds += Math.abs(c.amount);
				}
			}
		}
		return { total_concilia_deposits, total_concilia_refunds };
	}, [order?.concilia]);

	var selected_concilia = useMemo(()=>{
		if(!order?.concilia || !selectedDeposit) return null;
		return order.concilia.find(a=>selectedDeposit===a.deposit_id);
	}, [selectedDeposit, order?.concilia]);

	var refunding_tickets = useMemo(()=>{
		if(!order?.tickets || shownModal!==MODAL.ORDER_REFUND || !ticketsSelected || ticketsSelected.length==0) return [];
		return order.tickets.filter(a=>ticketsSelected.indexOf(a.ticket_id)>-1);
	}, [order?.tickets, ticketsSelected, shownModal]);

	var pending_refunds = useMemo(()=>{
		if(!order?.refunds) return [];
		return order.refunds.filter(a=>!a.executed && !a.cancelled);
	}, [order?.refunds]);

	var { refund_tickets_amount, refund_commission_amount, refund_total } = useMemo(()=>{
		var refund_tickets_amount = 0, refund_commission_amount = 0, refund_total = 0;
		if(refunding_tickets){
			for(var i of refunding_tickets){
				refund_tickets_amount += i.ticket_cost;
				refund_commission_amount += i.commission;
			}
			refund_total = refund_tickets_amount+(refundForm?.commission ? refund_commission_amount : 0)+(refundForm?.delivery ? order.delivery_cost : 0)+(refundForm?.payment ? total_payment_cost : 0);
		}
		return { refund_tickets_amount, refund_commission_amount, refund_total };
	}, [refunding_tickets, refundForm, order?.delivery_cost, total_payment_cost]);

	var is_printed_tickets = useMemo(()=>{
		if(!order?.payments || order.payments.length==0) return false;
		for(var i of order?.payments){
			if(i.payment_method!==PaymentMethods.PRINTED_TICKET){
				return false;
			}
		}
		return true;
	}, [order?.payments]);

	var not_used_tickets = useMemo(()=>{
		if(!order?.tickets || order.tickets.length==0) return [];
		return order.tickets.filter(a=>a.paid && !a.cancelled && a.uses==0);
	}, [order?.tickets]);

	var shown_refunds = useMemo(()=>{
		if(!order?.refunds) return [];
		return order.refunds.filter(a=>a.refund_type!==RefundType.CANCELLED)
	}, [order?.refunds]);

	var selected_paid_payment = useMemo(()=>{
		if(!SetPaidForm.data.payment_id || !order?.payments) return null;
		return order.payments.find(a=>a.payment_id===SetPaidForm.data.payment_id);
	}, [SetPaidForm.data.payment_id, order?.payments]);

	var select_ticket_modal = useMemo(()=>{
		if(!shownModal || !selectTicketType) return null;
		if(shownModal!==MODAL.TICKET_SELECT) return null;

		var data : {
			header: string,
			subheader: string,
			onComplete: (s: SetLoading)=>void,
			tickets: OrderDetails["tickets"],
			selected: OrderDetails["tickets"],
			hiding?: boolean,
			confirm: boolean,
			confirm_text: string,
		} = {
			header: null,
			onComplete: null,
			subheader: null,
			tickets: null,
			selected: null,
			confirm: false,
			confirm_text: null,
		}
		var tickets = order.tickets.filter(a=>!!a.paid && !a.cancelled);
		var selected = !!ticketsSelected ? order.tickets.filter(a=>ticketsSelected.indexOf(a.ticket_id)!=-1) : [];
		
		if(selectTicketType===SelectTicketType.CANCEL_TICKETS){
			data = {
				header: t('order.cancel_tickets.header'),
				subheader: t('order.cancel_tickets.subheader'),
				onComplete: cancelMultipleTickets,
				selected,
				tickets,
				confirm: true,
				confirm_text: t('order.cancel_tickets.confirm_text'),
			}
		}else if(selectTicketType===SelectTicketType.HIDE_TICKETS){
			var is_hiding = selected.findIndex(a=>!!a.hidden)==-1;
			data = {
				header: t(`order.hide_tickets.header_${is_hiding ? 'hide' : 'show'}`),
				subheader: t(`order.hide_tickets.subheader_${is_hiding ? 'hide' : 'show'}`),
				onComplete: s=>{
					return hideMultipleTickets(selected.map(a=>a.ticket_id), is_hiding, s);
				},
				hiding: is_hiding,
				tickets,
				selected,
				confirm: true,
				confirm_text: t(`order.hide_tickets.confirm_${is_hiding ? 'hide' : 'show'}_text`)
			}
		}else if(selectTicketType===SelectTicketType.REVIVE_TICKETS){
			var now = getNow().toSeconds();
			data = {
				header: t('order.revive_tickets.header'),
				subheader: t('order.revive_tickets.subheader'),
				onComplete: sendReviveTickets,
				tickets: order.tickets.filter(a=>!a.cancelled && (!a.paid || (a.date_expiration && a.date_expiration<now ))),
				selected,
				confirm: false,
				confirm_text: null
			}
		}
		return data;
	}, [selectTicketType, shownModal, ticketsSelected, confirmCheck]);

	var onOrderEditChange = bindFormChange(orderEdit, setOrderEdit);
	var onTicketEditChange = bindFormChange(ticket, setTicket);
	var onDepositFormChange = bindFormChange(depositForm, setDepositForm);
	var onRefundFormChange = bindFormChange(refundForm, setRefundForm);

	var is_free = tickets_paid>0 && tickets_free>=tickets_paid;

	if(!order || loadError) return (
		<Header 
			loading={!loadError} 
			subtext={loadError} 
			text={loadError ? 'Error' : 'Cargando orden...'} 
			iconName={loadError ? "face-frown-open" : null} 
			style={{ marginTop: 15 }} 
		/>
	)

	return <div className="ar order-view-screen">
		<Header text={`Orden ${order.order_hash}`} />
		{!!order.cancelled && (
			<Message header="Orden cancelada" type="error" centered style={{ width: 400, margin: 'auto', marginBottom: 10 }} />
		)}
		{!order.cancelled && user_access.tickets && (!order.tickets || order.tickets.length==0) && (
			<Message header="Orden sin boletos" type="info" centered style={{ width: 400, margin: 'auto', marginBottom: 10 }} />
		)}
		{!order.cancelled && !!order_expired && order.tickets.length>0 && (
			<Message header="Orden expirada" centered style={{ width: 400, margin: 'auto', marginBottom: 15 }}>
				{user_access.REVIVE_TICKET && <Button text="Revivir" color="black" onClick={showModal(MODAL.EXTEND)} style={{ marginTop: 5, width: 200 }} />}
			</Message>
		)}
		{(is_free && !order.cancelled) && (
			<Message header="Orden cortesía" type="info" centered style={{ width: 400, margin: 'auto', marginBottom: 10 }}>
				Esta orden tiene {tickets_free} boleto{tickets_free==1 ? '' : 's'} de cortesía
			</Message>
		)}
		{(is_printed_tickets && !order.cancelled) && (
			<Message header="Orden boleto duro" type="info" centered style={{ width: 400, margin: 'auto', marginBottom: 10 }}>
				Esta orden tiene {tickets_paid} boleto{tickets_paid==1 ? '' : 's'} de boleto duro
			</Message>
		)}
		{(total_tickets+total_commission+order.delivery_cost+total_payment_cost)!=payments_total && tickets_paid>0 && (tickets_paid-tickets_free)>0 ? (
			<Message header="Atención" type="info" style={{ width: 400, margin: 'auto', marginBottom: 10 }}>
				Los totales de la orden y lo pagado esta desfasado, favor de contactar desarrollo.
				<ul>
					<li>Total en orden: {addCommas(total_tickets+total_commission+total_payment_cost+order.delivery_cost)}</li>
					<li>Total en pagos: {addCommas(payments_total)}</li>
					<li>Diferencia: {addCommas(Math.abs((total_tickets+total_commission+total_payment_cost+order.delivery_cost)-payments_total))}</li>
				</ul>
			</Message>
		) : null}
		{pending_refunds.length>0 && (
			<Message header="Reembolso pendiente" centered style={{ width: 400, margin: 'auto', marginBottom: 10 }}>
				Esta orden tiene un reembolso pendiente de ser completada por el cliente. Se le envió a el cliente un correo electrónico con instrucciones de como completar su reembolso.
				<Button text="Ver reembolso" color="black" style={{ margin: 'auto', marginTop: 10, display: 'block', width: 200 }} as={Link} to={`/refunds/${pending_refunds[0].refund_id}`} />
			</Message>
		)}
		<Groupper title="Detalles de orden" fitted style={{ maxWidth: 600, margin: '15px auto' }}>
		{!order_expired ? (
			<Toolbar stretch divided className="order">
				<Toolbar.Item text="Marcar pagado" iconName="check-circle" onClick={showSetPaid(tickets_alive_pending.map(a=>a.ticket_id))} if={user_access.SET_TICKET_PAID && (!order.cancelled && tickets_paid==0 && tickets_pending>0)} />
				<Toolbar.Item text="Reenviar" iconName="envelope" onClick={showModal(MODAL.RESEND)} if={!order.cancelled && tickets_paid>0} />
				<Toolbar.Dropdown text="Editar" iconName="pen" items={[
					{ text: 'Cambiar datos', onClick: showOrderEdit, if: user_access.CHANGE_ORDER_DATA, value: null },
					{ text: 'Cambiar totales', onClick: showTotalsEdit, if: user_access.CHANGE_TOTALS, value: null },
					{ text: 'Cambiar entrega', onClick: showDeliveryChange, if: user_access.CHANGE_DELIVERY_METHOD, value: null },
				]} />
				<Toolbar.Item text="Reembolsar" iconName="hand-holding-dollar" onClick={showRefundForm} if={!order.cancelled && tickets_paid>0 && (user_access.refund || user_access.refund_request)} />
				<Toolbar.Dropdown text="Herramientas" iconName="wrench" items={[
					{ text: 'Ver PDF', 					onClick: viewOrderPdf, 								if: tickets_paid>0 && user_access.VIEW_PDF },
					{ text: 'Ver facturas', 			onClick: showModal(MODAL.ORDER_INVOICES),		if: user_access.VIEW_ORDERS },
					{ text: 'Ver correos', 				onClick: showModal(MODAL.ORDER_EMAILS),  		if: user_access.VIEW_ORDER_EMAILS },
					{ text: 'Convertir a cortesía',	onClick: showModal(MODAL.CONVERT_FREE),  		if: user_access.CREATE_FREE && tickets_paid>0 },
					{ text: 'Conciliar', 				onClick: showCreateDeposit, 						if: user_access.MANUAL_DEPOSIT },
					// { text: 'Mover boletos', 			onClick: showModal(MODAL.MOVE_TICKETS),  		if: user_access.MOVE_TICKETS },
					{ text: 'Revivir boletos',			onClick: showSelectTickets(SelectTicketType.REVIVE_TICKETS),		if: tickets_cancelled>0 && user_access.REVIVE_TICKET, className: 'green' },
					{ text: 'Expirar orden', 			onClick: showModal(MODAL.EXPIRE_ORDER),  		if: !order.cancelled && tickets_pending>0 && !order_expired && user_access.CANCEL_TICKET, className: 'red' },
					{ text: 'Esconder boletos',		onClick: showSelectTickets(SelectTicketType.HIDE_TICKETS), 		if: user_access.HIDE_ORDERS && tickets_paid>0 },
					{ text: 'Cancelar no usados', 	onClick: showModal(MODAL.CANCEL_NOT_USED),	if: user_access.CANCEL_UNUSED && is_printed_tickets, className: 'red' },
					{ text: 'Cancelar boletos',		onClick: showSelectTickets(SelectTicketType.CANCEL_TICKETS),	if: tickets_paid>0 && user_access.CANCEL_TICKET, className: 'red' },
					{ text: 'Cancelar orden',			onClick: showModal(MODAL.CANCEL_ORDER),  		if: !order.cancelled && tickets_paid>0 && user_access.CANCEL_TICKET, className: 'red' },
				]} />
			</Toolbar>
		) : null}
			<Table details striped>
				<Table.Body>
					<Table.Row data={['Identificador', <>
						<span className="fr mono">{order.order_hash}</span>
						<div className="meta">
							<span style={{ color: 'transparent' }}>:</span>
							ID: {order.order_id}
						</div>
					</>]} />
					{!!order.legacy_id && (
						<Table.Row style={{ color: 'brown' }} data={['Folio T2', <>
							<span className="fr mono" style={{ color: 'brown' }}>{order.legacy_id}</span>
							<div className="meta">
								<Link className="fr tiny red button" to={`https://admin.arema.mx/orders/${order.legacy_id}`} target='_blank'>Ver</Link>
							</div>
						</>]} />
					)}
					<Table.Row data={['Estatus', <>
						{(tickets_refunded==0 || tickets_refunded<order.tickets.length) && (
							<div className={`fr label ${order.cancelled ? 'red' : (order_expired ? '' : (tickets_paid>0 ? 'green' : 'blue'))}`} style={{ marginRight: 5 }}>
								{order.cancelled ? 'Cancelado' : (order_expired ? 'Expirado' : (tickets_paid>0 ? 'Pagado' : 'Pendiente'))}
							</div>
						)}
						{tickets_refunded>0 && (
							<div className="fr red label">Reembolso</div>	
						)}
					</>]} />
					{!!order.pdv_id && <Table.Row data={['PDV', <>
						[{order.pdv_id}] {order.pdv_name}
						<div className="meta">
							<Link className="fr tiny button" to={`/pdv/${order.pdv_id}`}>Ver PDV</Link>
						</div>
					</>]} />}
					{!!order.user_creator && <Table.Row data={['Vendedor', <>
						[{order.user_creator}] {order.user_creator_name}
						<div className="meta">
							<Link className="fr tiny button" to={`/users/${order.user_creator}`}>Ver usuario</Link>
						</div>
					</>]} />}
					<Table.Row data={['Nombre', order.first_name || order.last_name ? `${order.first_name} ${order.last_name}` : <i>Sin nombre</i>]} />
					<Table.Row data={['Correo', order.email || <i>Sin correo</i>]} />
					{!!order.phone && (
						<Table.Row data={['Teléfono', order.phone || <i>Sin teléfono</i>]} />
					)}
					<Table.Row data={['Creación', moment.unix(order.date_created).format('DD/MMM/YY HH:mm:ss')]} />
					{!!next_ticket_expiration && (
						<Table.Row data={['Expiración', <>
							{moment.unix(next_ticket_expiration).format('DD/MMM/YY HH:mm:ss')}
							<div className="meta">{moment.unix(next_ticket_expiration).fromNow(false)}</div>
						</>]} />
					)}
					{tickets_paid>0 && (
						<Table.Row data={['Pagado', moment.unix(last_ticket_paid_date).format('DD/MMM/YY HH:mm:ss')]} />
					)}
					<Table.Row data={['Forma de pago', (
						active_payments && active_payments.length>0 ? <>
							{uniqueArray(active_payments.map(a=>a.method_name_internal)).join(', ')}
							<div className="meta">
								ID: {uniqueArray(active_payments.map(a=>a.payment_method)).join(', ')}
							</div>
						</> : order.payment_method ? <>
							{order.payment_method_name_internal}
							<div className="meta">
								ID: {order.payment_method}
							</div>
						</>: (
							<i>Sin forma de pago</i>
						)
					)]} />
					<Table.Row data={['Entrega', (
						order.delivery_id ? <>
							{order.delivery_name}
							<div className="meta">
								ID: {order.delivery_id}
							</div>
						</> : <i>Sin forma de entrega</i>
					)]} />
					{!!order.delivery_needs_location && (
						<Table.Row data={['Dirección', (
							order.delivery_location ? (
								<Button size="tiny" text="Ver dirección" onClick={showModal(MODAL.ORDER_LOCATION)} />
							) : <>
								<span style={{ color: 'brown' }}><i>Sin dirección de entrega</i></span>
								<div className="meta">
									<Button text="Agregar dirección" size="tiny" onClick={showModal(MODAL.ORDER_LOCATION)} />
								</div>
							</>
						)]} />
					)}
				</Table.Body>
			</Table>
			<Table className="totals">
				<Table.Head data={[['Boletos', 'Comisión', 'Forma pago', 'Entrega', 'Total']]} />
				<Table.Body>
					<Table.Row data={[
						addCommas(total_tickets),
						addCommas(total_commission),
						addCommas(total_payment_cost),
						addCommas(order.delivery_cost),
						<b>{addCommas(total_final)}</b>
					]} />
				</Table.Body>
			</Table>
		</Groupper>
		{user_access.view_refund && shown_refunds && shown_refunds.length>0 && (
			<Table selectable style={{ margin: 'auto', marginTop: 15, maxWidth: 600 }}>
				<Table.Head
					title="Reembolsos"
					collapsingIndexes={[0, 2, 3]}
					data={[['ID', 'Fecha', 'Estatus', 'Cantidad']]}
				/>
				<Table.Body>
					{shown_refunds.map(a=>(
						<Table.Row key={`RFD-${a.refund_id}`} collapsingIndexes={[0, 2, 3]} compact as={Link} to={`/refunds/${a.refund_id}`} data={[
							a.refund_id,
							moment.unix(a.date_completed || a.date_created).format('DD/MMM/YY HH:mm'),
							<div className={classNames('fr label', {
								green: !a.cancelled && a.completed,
								blue: !a.cancelled && !a.completed && a.executed,
								yellow: !a.cancelled && !a.executed && !a.completed,
								red: a.cancelled,
							})}>
								{
									a.cancelled ? 'Cancelado' : 
									(a.completed ? 'Completado' : 
									(a.executed ? 'Pendiente' : 'Enviado'))
								}
							</div>,
							addCommas(a.total)
						]} />
					))}
				</Table.Body>
			</Table>
		)}
		{user_access.tickets && order.tickets && order.tickets.length>0 && (
			<Groupper className="tickets" fitted title="Boletos" style={{ margin: '15px auto', maxWidth: 800 }}>
				{groupBy(order.tickets, 'date_id', ['date', 'event_name', 'event_id']).map((a,i)=><div key={`tkt-ev-${a.event_id}`}>
					{i>0 && (
						<div style={{ height: 4, backgroundColor: '#EEE' }}></div>
					)}
					<Table details selectable>
						<Table.Body>
							<Table.Row as={Link} compact to={`/events/${a.event_id}`} data={['Evento', <>
								{a.event_name}
								<div className="meta">ID: {a.event_id}</div>
							</>]} />
							<Table.Row as={Link} compact to={`/events/${a.event_id}/dates/${a.date_id}`} data={['Calendario', <>
								{moment.unix(a.date).format('DD/MMM/YY HH:mm')}
								<div className="meta">ID: {a.date_id}</div>
							</>]} />
							<Table.Row selectable={false} data={['Total Evento', addCommas(a.values.reduce((a: number, b: Ticket)=>a+b.ticket_cost+b.commission, 0))]} />
						</Table.Body>
					</Table>
					<Table className="tickets">
						<Table.Head
							data={[['ID', 'Sección', 'Producto', 'Butaca', 'Estado', 'Usos', 'Precio', '']]}
							collapsingIndexes={[0, 3, 4, 5, 6, 7, 8]}
							centeredIndexes={[3, 5, 6, 7]}
						/>
						<Table.Body
							collapsingIndexes={[0, 3, 4, 5, 6, 7, 8]}
							centeredIndexes={[3, 5, 6, 7]}
							data={a.values.map((t: Ticket)=>{
								var ticket_expired = !t.paid && !!t.date_expiration && t.date_expiration<now;
								return [
									<>
										<div className="fr mono">{t.ticket_hash}</div>
										<div style={{ color: 'gray', fontSize: 12 }}>
											<div style={{ lineHeight: '10px', marginTop: 2 }}>ID: {t.ticket_id}</div>
											{t.seat_id ? <div>
												<Icon name="chair" />
												<span className="fr mono">{t.seat_id}</span>
											</div> : null}
										</div>
										{!!t.paid && !t.payment_id && (
											<div style={{ color: 'brown', fontSize: 12, lineHeight: '10px' }}>
												<Icon name="triangle-exclamation" style={{ marginRight: 2 }} /> Sin pago
											</div>
										)}
									</>,
									t.section_name,
									<>
										{t.ticket_date ? <div>
											{moment.unix(t.ticket_date).format('DD/MMM/YY HH:mm')}
										</div> : null}
										{t.price_name}
										{!!t.gift_id ? <div style={{ fontWeight: 'bold' }}>Cortesía</div> : null}
									</>,
									t.seat_id ? <>
										<div style={{ fontSize: 20, fontWeight: 'bold' }}>
											{formatSeatNumber(t.seat_row, t.seat_number)}
										</div>
										{t.seat_section ? (
											<div style={{ fontSize: 12 }}>{t.seat_section}</div>
										) : null}
									</> : <Icon color="grey" name="minus" />,
									<div className={`fr label ${t.cancelled ? 'red' : (ticket_expired ? 'transparent' : (t.paid ? 'green' : 'blue'))}`}>
										{t.hidden ? <Icon name="eye-slash" /> : null}
										{t.cancelled ? (t.cancel_reason===CancelReason.REFUNDED ? 'Reembolsado' : 'Cancelado') : (ticket_expired ? 'Expirado' : (t.paid ? 'Pagado' : 'Pendiente'))}
									</div>,
									t.uses>0 ? (
										<div>
											<Icon name="check" color="green" />
											{t.uses}
										</div>
									) : (
										<Icon name="remove" color="grey" />
									),
									addCommas(t.ticket_cost+t.commission),
									<Button size="tiny" iconName="cog" style={{ minWidth: 16 }} onClick={showTicketOptions(t)} />
								]
							})}
						/>
					</Table>
				</div>)}
			</Groupper>
		)}
		{user_access.payments && (
			<Table selectable style={{ margin: 'auto', marginTop: 15, maxWidth: 800 }}>
				<Table.Head
					title="Pagos" 
					data={[['ID', 'Método', 'Estatus', 'Fecha', 'Comisión', ...(total_refunded>0 ? ['Cantidad', 'Reembolso'] : []), 'Total']]}
				/>
				<Table.Body>
					{!!order.payments && order.payments.length==0 && (
						<Table.Cell empty row colSpan={100} value={'Esta orden no tiene pagos'} />
					)}
					{!!order.payments && order.payments.length>0 && (
						order.payments.filter(a=>(!!a.executed || a.paid || paid_payments==0)).map(a=>(
							<Table.Row
								key={`ORDPY-${a.payment_id}`}
								collapsingIndexes={[0, 2, 3, 4, 5, 6, 7]}
								centeredIndexes={[0, 2, 3, 4, 5, 6, 7]}
								onClick={()=>onOpenPaymentModal(a.payment_id)}
								data={[
									a.payment_id,
									<>
										{dev ? `[${a.payment_method}] ` : null}
										{a.method_name_internal}
									</>,
									<div className={`fr label ${a.cancelled ? 'red' : (a.paid ? 'green' : 'transparent')}`}>
										{a.cancelled ? 'Cancelado' : (a.paid ? 'Pagado' : 'Pendiente')}
									</div>,
									moment.unix(a.date_paid || a.date_created).format('DD/MM/YY HH:mm'),
									addCommas(a.payment_cost),
									...(total_refunded>0 ? [
										addCommas(a.total),
										addCommas(a.total_refunded),
									] : []),
									addCommas(a.total_available),
								]}
							/>
						))
					)}
				</Table.Body>
			</Table>
		)}
		{!!user_access.concilia && ((order.payments && order.payments.length>0) || (order.concilia && order.concilia?.length>0)) && (
			<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 700 }}>
				<Table.Head title="Conciliación" />
				<Table.Body>
					<Table.Row key={'dep-1'} details style={{ borderBottom: '1px solid #f0f0f0' }}>
						<Table.Cell key={'dep-1-1'} colSpan={2}>Depositos</Table.Cell>
						<Table.Cell key={'dep-1-2'} colSpan={5} className={classNames({
							red: total_paid<total_concilia_deposits
						})}>
							{addCommas(total_concilia_deposits)} / {addCommas(total_paid)}
							<div className="meta">
								{Math.round((total_concilia_deposits*100)/total_paid)}%
							</div>
						</Table.Cell>
					</Table.Row>
					{(total_concilia_refunds>0 || total_refunded>0) && (
						<Table.Row key={'dep-2'} details>
							<Table.Cell key={'dep-2-1'} colSpan={2}>Reembolsos</Table.Cell>
							<Table.Cell key={'dep-2-2'} colSpan={5} className={classNames({
								red: total_refunded<total_concilia_refunds
							})}>
								{addCommas(total_concilia_refunds)} / {addCommas(total_refunded)}
								<div className="meta">
									{Math.round((total_concilia_refunds*100)/total_refunded)}%
								</div>
							</Table.Cell>
						</Table.Row>
					)}
					<Table.Row header data={['ID','Tipo','Método','Fecha','Cantidad','']} />
					{order.concilia && order.concilia.length>0 ? <>
						{order.concilia.map(a=>(
							<Table.Row collapsingIndexes={[0, 1, 3, 4, 5]} key={`conc-${a.deposit_id}`} data={[
								a.deposit_id, a.deposit_type_name, a.payment_method_name, moment.unix(a.date_deposit).format('DD/MMM/YY'), addCommas(a.amount),
								<Button icon iconName="ellipsis-h" size="tiny" menu={[
									{ text: 'Eliminar', onClick: showDeleteDeposit(a.deposit_id) }
								]} />
							]} />
						))}
					</> : (
						<Table.Cell colSpan={6} row empty>Esta orden no tiene conciliación</Table.Cell>
					)}
				</Table.Body>
			</Table>
		)}
		<Modal open={shownModal==MODAL.RESEND} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Reenviar boletos</Modal.Header>
			<Modal.Content>
				<Header centered text="¿Reenviar el correo al cliente?" subtext={`Se le reenviará el correo ${tickets_paid>0 ? 'con los boletos' : 'de las instrucciónes de pago'} a el cliente.`} />
				<Input label="Correo" value={order.email} readonly />
				{/* {!order.delivery_id && (
					<Message type="error" text="La orden no tiene una forma de entrega asignada" centered />
				)} */}
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Reenviar" color="black" onClick={resend} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal==MODAL.SET_PAID && tickets_pending>0} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Marcar pagado</Modal.Header>
			<Modal.Content>
				<Header text="Marcar boletos pagados" subtext={'Marcará los siguientes boletos como pagados'} style={{ marginBottom: 0 }} />
				<div style={{ textAlign: 'center', color: 'gray', fontWeight: 'bold', marginBottom: 10 }}>No se le mandará correo al cliente</div>
				<Table striped style={{ marginBottom: 15 }}>
					<Table.Head data={[['ID', 'Sección', 'Producto', 'Total']]} />
					<Table.Body>
						{order.tickets.filter(a=>SetPaidForm.data.tickets.indexOf(a.ticket_id)!=-1).map(a=>(
							<Table.Row key={`STPD-${a.ticket_id}`} collapsingIndexes={[0, 2, 3]} data={[
								a.ticket_hash,
								a.section_name,
								a.price_name,
								addCommas(a.ticket_cost+a.commission)
							]} />
						))}
					</Table.Body>
				</Table>
				<Dropdown
					label="Pago registrado" 
					comment="Es importante seleccionar correctamente el pago que se le asignarán a los boletos, ya que la conciliación de los boletos depende de ello"
					onValueChange={v=>{
						if(v==-SetPaidType.CREATE_PAYMENT){
							SetPaidForm.setValue('type', SetPaidType.CREATE_PAYMENT);
							SetPaidForm.setValue('payment_method', null);
						}else if(v==-SetPaidType.CONVERT_FREE){
							SetPaidForm.setValue('type', SetPaidType.CONVERT_FREE);
						}else{
							SetPaidForm.setValue('type', SetPaidType.ADD_PAYMENT);
							SetPaidForm.setValue('payment_id', v);
						}
					}}
					value={SetPaidForm.data.payment_id}
					items={[
						...(order.payments ? order.payments.map(a=>({
							text: `Pago #${a.payment_id} (${a.method_name_internal})`,
							meta: a.payment_hash || fromUnix((a.date_paid || a.date_created)).toFormat('dd/MMM/yy HH:mm'),
							value: a.payment_id,
						})) : []),
						{ text: <i>Nuevo pago</i>, value: -SetPaidType.CREATE_PAYMENT },
						...(user_access.CREATE_FREE ? [{
							text: <i>Convertir cortesía</i>,
							value: -SetPaidType.CONVERT_FREE,
						}] : [])
					]}
				/>
				{SetPaidForm.data.type==SetPaidType.CREATE_PAYMENT && (
					<SearchPaymentMethodDropdown
						value={SetPaidForm.data.payment_method}
						onValueChange={(v)=>SetPaidForm.setValue('payment_method', v)}
					/>
				)}
				{!!selected_paid_payment && (
					<Table details style={{marginBottom: 15 }}>
						<Table.Head title="Pago seleccionado" titleSize="small" />
						<Table.Body>
							<Table.Row data={['ID', selected_paid_payment.payment_id]} />
							<Table.Row data={['Método', selected_paid_payment.method_name_internal]} />
							<Table.Row data={['Estatus', <Label color={!!selected_paid_payment.paid ? 'green' : 'blue'}>{!!selected_paid_payment.paid ? 'Pagado' : 'Pendiente'}</Label>]} />
							<Table.Divider />
							<Table.Row data={['Comisión', addCommas(selected_paid_payment.payment_cost)]} />
							<Table.Row data={['Entrega', addCommas(selected_paid_payment.delivery_cost)]} />
							<Table.Row data={['Total', addCommas(selected_paid_payment.total)]} />
						</Table.Body>
					</Table>
				)}
				{SetPaidForm.data.type===SetPaidType.CONVERT_FREE && (
					<Input label="Descripción" comment="Ingresa el motivo de la cortesía" value={SetPaidForm.data.gift_description} onChange={SetPaidForm.onDataChange('gift_description')} />
				)}
				{!!(SetPaidForm.data.type || SetPaidForm.data.payment_id) && (
					<Message type="info" centered>
						{SetPaidForm.data.type==SetPaidType.CREATE_PAYMENT ? <>
							Se creará un nuevo registro de pago con el método de pago seleccionado. Se le asignará esta forma de pago a los boletos que se marcarán como pagados
						</> : SetPaidForm.data.type==SetPaidType.CONVERT_FREE ? <>
							Los boletos <b>serán convertidos a cortesía</b>
						</> : <>
							Los boletos serán asignados al pago seleccionado
						</>}
					</Message>
				)}
				{selected_paid_payment && !selected_paid_payment.paid && (
					<Message centered type="warning" style={{ marginTop: 10 }}>
						El pago seleccionado ({selected_paid_payment.payment_id}) <b>no se encuentra pagado</b>, si se continua <b>se marcará como pagado</b>
					</Message>
				)}
				<Message style={{ marginTop: 15 }} list={SetPaidForm.prompts} type="error" />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Marcar pagada" color="black" onClick={setPaid} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal==MODAL.EXTEND} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>{order_expired ? 'Revivir' : 'Extender'} Orden</Modal.Header>
			<Modal.Content>
				<Header text={`${order_expired ? 'Revivir' : 'Extender'} orden`} subtext={'Se le agregará tiempo a la orden para realizar el pago.'} />
				{order_expired ? (
					<Message type="info" centered style={{ marginBottom: 10 }}>
						Como esta orden ya se expiró, se tendrá que revisar si los boletos están disponibles para ser revividos.
						{tickets_cancelled>0 && <>
							<br /> <b>Los boletos cancelados se mantendrán cancelados</b>
						</>}
					</Message>
				) : null}
				<Dropdown 
					label="Tiempo a agregar" 
					comment={order_expired ? 'Tiempo a agregar para realizar el pago una vez se haya revivido la orden.' : null}placeholder="Tiempo a agregar" 
					value={extendAmount} 
					onValueChange={setExtendAmount} 
					items={[5, 10, 30, 60, 120, 1440, 2880].map(a=>({
						value: a*60,
						text: a>=60 ? `${Math.floor(a/60)} hora${Math.floor(a/60)==1 ? '' : 's'}` : `${a} minutos`
					}))} 
				/>
				<Message style={{ marginTop: 15 }} list={modalPrompts} type="error" />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text={order_expired ? 'Revivir' : 'Extender'} color="black" onClick={sendExtend} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.VIEW_PDF} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Ver PDF</Modal.Header>
			<Modal.Content>
				{orderPdf ? (
					<div>
						<Header size="small" text={`PDF ${order.order_hash}`} subtext="En seguida se muestra el enlace para ver el pdf de los boletos." />
						<Input readonly value={orderPdf} onFocus={(e)=>e.target.select()} buttons={<>
							<Button icon iconName="copy" onClick={()=>{
								navigator.clipboard.writeText(orderPdf).then(()=>{
									Toast.success(t('toasts.clipboard_success'))
								}, ()=>Toast.error(t('toasts.clipboard_error')))
							}} />
							<Button icon iconName="external-link" onClick={()=>window.open(orderPdf, '_blank')} />
						</>} />
					</div>
				) : (
					<Header loading text="Cargando pdf..." />
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.ORDER_EDIT} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Editar orden {order.order_hash}</Modal.Header>
			{orderEdit && <Modal.Content>
				<Field amount={2}>
					<Input label="Nombre" value={orderEdit.first_name} onChange={onOrderEditChange('first_name')} />
					<Input label="Apellido" value={orderEdit.last_name} onChange={onOrderEditChange('last_name')} />
				</Field>
				{user_access.CHANGE_ORDER_EMAIL && (
					<Input label="Correo electrónico" value={orderEdit.email} onChange={onOrderEditChange('email')} />
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendOrderEdit} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal==MODAL.ORDER_TOTALS} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Editar costos {order.order_hash}</Modal.Header>
			{orderEdit && <Modal.Content>
				<Input label="Boletos" readonly value={total_tickets} />
				<Field amount={2}>
					<Input label="Comisión" readonly value={total_commission} />
					<Input label="Forma de pago" readonly value={total_payment_cost} />
				</Field>
				<Input label="Entrega" value={orderEdit.delivery_cost} onChange={onOrderEditChange('delivery_cost')} />
				<Stat label="Total" value={addCommas(total_tickets+total_commission+total_payment_cost+(parseFloat(orderEdit.delivery_cost.toString()) || 0))} style={{ paddingBottom: 0 }} />
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendTotalsEdit} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal==MODAL.ORDER_PAYMENT_EDIT} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Editar forma de pago</Modal.Header>
			{orderEdit && <Modal.Content>
				{paymentMethods ? (
					<Dropdown label="Forma de pago" value={orderEdit.payment_method} onValueChange={onOrderEditChange('payment_method')} items={paymentMethods.map(a=>({
						value: a.method_id,
						text: a.method_name_internal,
						meta: `ID: ${a.method_id}`,
					}))} />
				) : (
					<Header loading text="Cargando formas de pago..." />
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendPaymentChange} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal==MODAL.ORDER_DELIVERY_EDIT} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Editar entrega</Modal.Header>
			{orderEdit && <Modal.Content>
				{deliveryMethods ? (
					<Dropdown 
						label="Forma de entrega" 
						value={orderEdit.delivery_id} 
						onValueChange={onOrderEditChange('delivery_id')} 
						items={deliveryMethods.map(a=>({
							value: a.delivery_id,
							text: a.method_name,
							meta: `ID: ${a.delivery_id}`,
						}))}
					/>
				) : (
					<Header loading text="Cargando formas de entrega..." />
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendDeliveryChange} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.CONVERT_FREE} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Convertir a cortesía</Modal.Header>
			<Modal.Content>
				<Header size="small" text="Convertir a cortesía" subtext="Esto podrá el costo de todos los boletos, cargos por servicio, cargos de entrega y pago en 0." />
				<Input value={freeComments} onChange={setFreeComments} label="Descripción" comment="Comentarios de el origen de las cortesías" textarea />
				<Checkbox label="Esta acción es la que deseo realizar." checked={confirmCheck} onChange={setConfirmCheck} />
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Convertir" color="red" onClick={sendConvertFree} disabled={!confirmCheck} />
			</Modal.Actions>
		</Modal>
		{/* <Modal open={shownModal===MODAL.MOVE_TICKETS} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Mover boletos</Modal.Header>
			<Modal.Content>
				{MoveOrderForm.data.moved ? (
					<Header
						iconName="check-circle"
						style={{ fontSize: 20, marginTop: 15 }} 
						text={'Boletos movidos'} 
						subtext={`Se han movido los boletos a la orden ingresada: ${MoveOrderForm.data.order_hash}`} 
						subtextStyle={{ fontSize: 14 }} 
						actions={(
							<Button text="Ver orden" color="black" style={{ fontSize: 14, width: 200 }} as={Link} to={`/orders/${MoveOrderForm.data.order_hash}`} />
						)}
					/>
				) : <>
					<Header size="small" text="Mover boletos" subtext="Mover los boletos a otra orden." style={{ marginTop: 0 }} />
					{order.tickets && order.tickets.length>0 ? (
						null
						// <Items selectable striped toggle
						// 	style={{ marginTop: 15 }}
						// 	data={tickets_alive}
						// 	valueExtractor={a=>a.ticket_id}
						// 	selected={MoveOrderForm.data.tickets}
						// 	onToggle={MoveOrderForm.onDataChange('tickets')}
						// 	render={(a)=><TicketItem ticket={a} />} 
						// />
					) : null}
					<Input label="Orden" comment="Orden en donde terminarán los boletos" style={{ marginTop: 15 }} value={MoveOrderForm.data.order_hash} onChange={MoveOrderForm.onDataChange('order_hash')} />
					<Message type="error" list={MoveOrderForm.prompts} style={{ marginTop: 15 }} />
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				{!MoveOrderForm.data.moved && (
					<Button text="Mover" color="black" onClick={sendMoveTickets} />
				)}
			</Modal.Actions>
		</Modal> */}
		<Modal open={shownModal===MODAL.EXPIRE_ORDER} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Expirar orden</Modal.Header>
			<Modal.Content>
				<Header size="small" text="Expirar orden" subtext="Esto podrá el tiempo restante para realizar el pago en 0" />
				{total_paid>0 && (
					<Message header="¡Atención!" text="Esta orden ya fue pagada, favor de asegurarse de que esta es la acción que se desea realizar." type="error" centered style={{ marginTop: 15, marginBottom: 15 }} />
				)}
				<Checkbox label="Esta acción es la que deseo realizar" checked={confirmCheck} onChange={setConfirmCheck} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Expirar orden" color="black" onClick={sendExpireOrder} disabled={!confirmCheck} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.CANCEL_ORDER} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Cancelar orden</Modal.Header>
			<Modal.Content>
				<Header size="small" text="Cancelar orden" subtext="Esto cancelará la orden, sus boletos, sus pagos y liberará las butacas." />
				{total_paid>0 && (
					<Message header="¡Atención!" text="Esta orden ya fue pagada, favor de asegurarse de que esta es la acción que se desea realizar." type="error" centered style={{ marginTop: 15, marginBottom: 15 }} />
				)}
				<Checkbox label="Esta acción es la que deseo realizar" checked={confirmCheck} onChange={setConfirmCheck} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Cancelar orden" color="red" onClick={sendCancelOrder} disabled={!confirmCheck} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.CANCEL_TICKET} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Cancelar boleto <span className="fr mono">{ticket?.ticket_hash}</span></Modal.Header>
			{!!ticket && <Modal.Content>
				<Header size="small" text="Cancelar boleto" subtext="Se cancelará el boleto y se descontará la cantidad del pago correspondiente" />
				<Table details>
					<Table.Head title="Boleto" titleSize="small" />
					<Table.Body data={[
						['ID', ticket.ticket_id],
						['Hash', <div className="fr mono">{ticket.ticket_hash}</div>],
						['Evento', ticket.event_name],
						['Fecha', fromUnix((ticket.ticket_date || ticket.date)).toFormat('dd/MMM/yy HH:mm')],
						['Area', ticket.section_name],
						['Producto', ticket.price_name],
						...(ticket.seat_id ? [
							['Butaca', <>
								{formatSeatNumber(ticket.seat_row, ticket.seat_number)}
								{ticket.seat_section ? ` - ${ticket.seat_section}` : ''}
								<div className="meta">{ticket.seat_id}</div>
							</>]
						] : [])
					]} />
				</Table>
				{!!ticket.paid && (
					<Message header="¡Atención!" text="Este boleto ya fue pagado, favor de asegurarse de que esta es la acción que se desea realizar." type="error" centered style={{ marginTop: 15, marginBottom: 15 }} />
				)}
				<Checkbox label="Esta acción es la que deseo realizar" checked={confirmCheck} onChange={setConfirmCheck} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Cancelar orden" color="red" onClick={cancelSingleTicket} disabled={!confirmCheck} />
			</Modal.Actions>
		</Modal>
		{is_printed_tickets && (
			<Modal open={shownModal===MODAL.CANCEL_NOT_USED} onClose={bindClick(setShownModal)} size="mini">
				<Modal.Header>Cancelar boletos no usados</Modal.Header>
				<Modal.Content>
					{not_used_tickets.length>0 ? <>
						<Header size="small" text="Cancelar boletos no usados" subtext="Esto cancelará TODOS los boletos no usados de esta orden" />
						{total_paid>0 && (
							<Message type="error" centered style={{ marginTop: 15, marginBottom: 15 }}>
								Se cancelarán {not_used_tickets.length} boleto{not_used_tickets.length==1 ? '' : 's'} no usados
							</Message>
						)}
						<Checkbox label="Esta acción es la que deseo realizar" checked={confirmCheck} onChange={setConfirmCheck} />
						<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
					</> : (
						<Header style={{ height: 150 }} text={'Sin boletos no usados'} subtext={'La orden no cuenta con boletos no usados'} iconName={'ban'} />
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
					{not_used_tickets.length>0 && (
						<Button text="Cancelar boletos" color="red" onClick={sendCancelNotUsed} disabled={!confirmCheck} />
					)}
				</Modal.Actions>
			</Modal>
		)}
		{/* <Modal open={shownModal===MODAL.REVIVE_TICKETS} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Revivir boletos</Modal.Header>
			<Modal.Content>
				<Header size="small" text="Revivir boletos" subtext="El sistema revisará si el boleto puede ser revivido (cupo, butaca libre) y marcará el boleto como no cancelado. Si el boleto no ha sido pagado no se marcará como pagado." style={{ marginTop: 0 }} />
				{order.tickets && order.tickets.length>0 ? (
					null
					// <Items selectable striped toggle
					// 	style={{ marginTop: 15 }}
					// 	data={order.tickets.filter(a=>!!a.cancelled)}
					// 	valueExtractor={a=>a.ticket_id}
					// 	selected={ticketsSelected}
					// 	onToggle={setTicketsSelected}
					// 	render={(a)=><TicketItem ticket={a} />} 
					// />
				) : null}
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Revivir boletos" color="black" onClick={sendReviveTickets} disabled={ticketsSelected.length==0} />
			</Modal.Actions>
		</Modal> */}
		<Modal open={!!ticket && shownModal==MODAL.TICKET_OPTIONS} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Boleto {ticket?.ticket_hash}</Modal.Header>
			{!!ticket && (
				<Modal.Content style={{ padding: 0 }}>
					<div className="fr selectable striped divided items">
						<ToolbarItem text="Ver detalles" icon="ticket" onClick={showModal(MODAL.TICKET_DETAILS)} />
						{user_access.SET_TICKET_PAID && !ticket.paid && <ToolbarItem text="Marcar pagado" icon="check-circle" onClick={showSetPaid([ticket?.ticket_id])} />}
						{user_access.VIEW_TICKET_FORM && <ToolbarItem text="Ver formulario" icon="clipboard-list" onClick={showTicketForm} />}
						{user_access.VIEW_TICKET_USES && <ToolbarItem text="Ver usos" icon="qrcode" onClick={showTicketUses} />}
						{user_access.USE_TICKET && <ToolbarItem text="Usar boleto" icon="check-square" onClick={showUseTicket} />}
						{user_access.EDIT_TICKET_PRICE && <ToolbarItem text="Editar precio" icon="dollar-sign" onClick={showModal(MODAL.TICKET_EDIT)} />}
						<ToolbarItem text="Ver impresiones" icon="print" onClick={showModal(MODAL.TICKET_PRINTS)} />
						{user_access.CHANGE_TICKET_SEAT && (
							!!ticket.seat_id ? (
								<ToolbarItem text="Re-Asignar Butaca" icon="exchange" onClick={showModal(MODAL.TICKET_UPDATE_SEAT)} />
							) : (
								<ToolbarItem text="Mover fecha boleto" icon="exchange" onClick={showModal(MODAL.TICKET_MOVE_DATE)} />
							)
						)}
						{user_access.HIDE_ORDERS &&
							<ToolbarItem text={ticket?.hidden ? "Mostrar Boleto" : "Esconder Boleto"} icon={ticket?.hidden ? 'eye' : 'eye-slash'} onClick={showModal(MODAL.TICKET_HIDE)} />
						}
						<ToolbarItem text="Usuarios" icon="user" onClick={showModal(MODAL.TICKET_USER)} />
						{user_access.CANCEL_TICKET &&
							<ToolbarItem text={'Cancelar boleto'} icon={'ban'} onClick={showModal(MODAL.CANCEL_TICKET)} style={{ color: 'brown' }} />
						}
					</div>
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button onClick={bindClick(setShownModal)} text="Cerrar" />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticket && shownModal===MODAL.TICKET_DETAILS} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Boleto {ticket?.ticket_hash}</Modal.Header>
			{ticket && <Modal.Content>
				<Table fitted striped details>
					<Table.Body>
						<Table.Row data={['Identificador', ticket.ticket_hash]} />
						<Table.Row data={['Estatus', ticket.paid ? 'Pagado' : (ticket.cancelled ? 'Cancelado' : (ticket.date_expiration && ticket.date_expiration<now ? 'Expirado' : 'Pendiente'))]} />
						{!!ticket.cancelled && (
							<Table.Row data={['Razón cancelado', t(`cancel_reason.reason_${ticket.cancel_reason || 1}`)]} />
						)}
						<Table.Divider />
						<Table.Row data={['Event', `[${ticket.event_id}] ${ticket.event_name}`]} />
						<Table.Row data={['Fecha', `[${ticket.date_id}] ${moment.unix(ticket.ticket_date || ticket.date).format('DD/MMM/YY HH:mm')}`]} />
						<Table.Row data={['Producto', `[${ticket.price_id}] ${ticket.price_name}`]} />
						<Table.Row data={['Section', `[${ticket.section_id}] ${ticket.section_name}`]} />
						{!!ticket.seat_id && <>
							<Table.Row data={['IDButaca', ticket.seat_id]} />
							<Table.Row data={['Butaca Sección', ticket.seat_section]} />
							<Table.Row data={['Butaca Fila', ticket.seat_row]} />
							<Table.Row data={['Butaca Número', ticket.seat_number]} />
						</>}
						<Table.Divider />
						<Table.Row data={['Costo', addCommas(ticket.ticket_cost)]} />
						<Table.Row data={['Comisión', addCommas(ticket.commission)]} />
						<Table.Row data={['Costo Final', `${addCommas(ticket.ticket_cost+ticket.commission)}`]} />
					</Table.Body>
				</Table>
				<UserLog user={ticket} fitted style={{ marginTop: 0 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticket && shownModal===MODAL.TICKET_FORM} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Formulario Boleto {ticket?.ticket_hash}</Modal.Header>
			{ticket && <Modal.Content>
				{ticketForm ? (
					<TicketForm form={ticketForm} onChange={setTicketForm} />
				) : (
					<Header loading text="Cargando formulario" />
				)}
				<Message list={modalPrompts} type="error" />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendFormEdit} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticket && shownModal===MODAL.TICKET_EDIT} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Editar Boleto {ticket?.ticket_hash}</Modal.Header>
			{ticket && <Modal.Content>
				<Input label="Precio" value={ticket.ticket_cost} onChange={onTicketEditChange('ticket_cost')} />
				<Input label="Comissión" value={ticket.commission} onChange={onTicketEditChange('commission')} />
				<Stat label="Precio final boleto" value={addCommas((parseFloat(ticket.ticket_cost.toString()) || 0)+(parseFloat(ticket.commission.toString()) || 0))} style={{ paddingBottom: 10}} />
				<Message list={modalPrompts} type="error" style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
				<Button text="Guardar" color="black" onClick={sendTicketEdit} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticket && shownModal===MODAL.TICKET_USES} onClose={bindClick(setShownModal)}>
			<Modal.Header>Usos Boleto {ticket?.ticket_hash}</Modal.Header>
			{ticket && <Modal.Content>
				{ticketUses ? (
					ticketUses.length>0 ? (
						<Table fitted>
							<Table.Head
								data={[['ID', 'Mensaje', 'Usuario', 'Scanner', 'Fecha', ...(
									user_access.DELETE_TICKET_USE ? [
										<Icon name="times" />
									] : []
								)]]}
							/>
							<Table.Body 
								collapsingIndexes={[0, 2, 3, 4, 5]}
								centeredIndexes={[5]}
								data={ticketUses.map(a=>([
									a.use_id,
									a.message ? a.message : (
										<Icon name="minus" color="grey" />
									),
									a.user_creator ? (
										<Link className="normal" to={`/users/${a.user_creator}`} target="_blank">
											{a.user_creator_name}
										</Link>
									) : (
										<Icon name="minus" color="grey" />
									),
									a.scanner_id ? (
										<Link className="normal" to={`/scanner/${a.scanner_id}`} target="_blank">
											{a.scanner_name}
										</Link>
									) : (
										<Icon name="minus" color="grey" />
									),
									moment.unix(a.date_created).format('DD/MMM/YY HH:mm:ss'),
									...(user_access.DELETE_TICKET_USE ? [
										<Button size="tiny" icon iconName="times" onClick={bindClick(setTicketUseSelected, a)} />
									] : [])
								]))}
							/>
						</Table>
					) : (
						<Header text="Este boleto no tiene usos" iconName="smile" size="small" />	
					)
				) : (
					<Header loading text="Cargando usos del boleto" />
				)}
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticket && shownModal===MODAL.TICKET_NEW_USE} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Usar boleto {ticket?.ticket_hash}</Modal.Header>
			<Modal.Content>
				<Header size="small" text="Usar boleto" subtext="Esto agregará un uso a el boleto." style={{ marginTop: 0 }} />
				<Input label="Fecha de uso" calendar={{
					date: ticketUseDate,
					onSelected: setTicketUseDate,
					mode: 'datetime'
				}} />
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Usar boleto" color="black" onClick={sendUseTicket} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!ticketUseSelected} onClose={bindClick(setTicketUseSelected, null)} size="mini">
			<Modal.Header>Eliminar uso boleto</Modal.Header>
			{!!ticketUseSelected && <Modal.Content>
				<Header text={`Eliminar uso #${ticketUseSelected.use_id}`} subtext="Esto eliminará este uso en especifico del boleto." />
				<Table details>
					<Table.Head title="Detalles" titleSize="small" />
					<Table.Body>
						<Table.Row data={['ID', ticketUseSelected.use_id]} />
						<Table.Row data={['Boleto', ticket?.ticket_hash]} />
						<Table.Row data={['Fecha uso', moment.unix(ticketUseSelected.date_created).format('DD/MMM/YY HH:mm')]} />
						{ticketUseSelected.user_creator && (
							<Table.Row>
								<Table.Cell collapsing>Usuario</Table.Cell>
								<Table.Cell>
									<Link target="_blank" className="normal" to={`/users/${ticketUseSelected.user_creator}`}>
										{ticketUseSelected.user_creator_name}
									</Link>
								</Table.Cell>
							</Table.Row>
						)}
						{ticketUseSelected.scanner_id && (
							<Table.Row>
								<Table.Cell collapsing>Scanner</Table.Cell>
								<Table.Cell>
									<Link className="normal" target="_blank" to={`/scanners/${ticketUseSelected.scanner_id}`}>{ticketUseSelected.scanner_name}</Link>
									<div className="meta">ID: {ticketUseSelected.scanner_id}</div>
								</Table.Cell>
							</Table.Row>
						)}
					</Table.Body>
				</Table>
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setTicketUseSelected, null)} />
				<Button text="Eliminar" color="red" onClick={deleteTicketUse} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.CONCILIA} onClose={bindClick(setShownModal)} size="small" className="order-payment-concilia-manual">
			<Modal.Header>Conciliar orden</Modal.Header>
			{shownModal===MODAL.CONCILIA && <Modal.Content>
				<Header size="small" text="Conciliar orden" subtext="Conciliar los depositos o reembolsos de dinero de una orden." style={{ marginTop: 0, marginBottom: 15 }} />
				<Dropdown label="Tipo de deposito" value={depositForm.type} onValueChange={onDepositFormChange('type')} items={[
					{ value: DepositType.DEPOSIT, text: 'Deposito' },
					{ value: DepositType.REFUND, text: 'Reembolso' },
					{ value: DepositType.CHARGEBACK, text: 'Contracargo' },
				]} />
				{depositForm.type && <>
					<Table style={{ marginTop: 15, overflow: 'hidden' }}>
						<Table.Head title="Forma de pago" />
						<Table.Body>
							{active_payments.map(a=>{
								var deposits = order.concilia.filter(b=>b.payment_id==a.payment_id);
								var payment_total = depositForm.type===DepositType.DEPOSIT ? a.total : a.total_refunded;
								var deposit_charge = 0, deposit_refund = 0;
								
								for(var i of deposits){
									if(i.deposit_type===DepositType.DEPOSIT){
										deposit_charge += i.amount;
									}else{
										deposit_refund += i.amount;
									}
								}
								var deposit_amount = depositForm.type===DepositType.DEPOSIT ? deposit_charge : deposit_refund;
								return <Table.Row selectable onClick={()=>onDepositFormChange('payment_id')(a.payment_id)}>
									<Table.Cell collapsing>
										<Icon style={{ margin: 0 }} size={18} name={depositForm.payment_id===a.payment_id ? 'circle-check' : 'circle'} solid={depositForm.payment_id===a.payment_id} />
									</Table.Cell>
									<Table.Cell collapsing>{a.payment_id}</Table.Cell>
									<Table.Cell>
										<b>{a.method_name_internal}</b>
										<br />
										Depositado: <Money value={deposit_amount} />
									</Table.Cell>
									<Table.Cell collapsing style={{ textAlign: 'right' }}>
										<Money value={payment_total-Math.abs(deposit_amount)} />
										<div style={{ color: 'gray' }}>Total: <Money value={payment_total} /></div>
									</Table.Cell>
								</Table.Row>
							})}
						</Table.Body>
					</Table>
					<Message type="info" centered style={{ marginTop: 15 }}>
						{(!!depositForm?.type && (depositForm?.type===DepositType.DEPOSIT ? total_concilia_deposits>=total_paid : (Math.abs(total_concilia_refunds)>=Math.abs(total_refunded)))) && (
							<div style={{ paddingBottom: 10 }}>Esta orden ya había conciliada por completo</div>
						)}
						<Stat.Group>
							<Stat 
								className="small" 
								label={'Conciliado'} 
								style={{ padding: 0 }} 
								value={<>
									{addCommas(total_concilia_deposits+(parseFloat(depositForm.amount) || 0))}
									<span style={{ fontWeight: 'normal', fontSize: 20, lineHeight: 0 }}>/{addCommas(DepositType.DEPOSIT ? total_paid : Math.abs(total_refunded))}</span>
								</>} 
							/>
						</Stat.Group>
					</Message>
					{!!depositForm.payment_id && <>
						<Field amount={2} style={{ marginTop: 15 }}>
							<Input label="Cantidad" value={depositForm.amount} onChange={onDepositFormChange('amount')} />
							<Input label="Fecha de deposito" calendar={{
								date: depositForm.date_deposit,
								mode: 'date',
								onSelected: onDepositFormChange('date_deposit'),
							}} />
						</Field>
						<Input label="Referencia" placeholder="(Opcional) Numero de control o referencia de la forma de pago" value={depositForm?.external_id} onChange={onDepositFormChange('external_id')} />
					</>}
				</>}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Concilia" color="black" disabled={!depositForm?.amount || parseFloat(depositForm?.amount)<=0} onClick={createDeposit} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.ORDER_REFUND} onClose={bindClick(setShownModal)} size="tiny" className="refund-create">
			<Modal.Header>Reembolsar boletos</Modal.Header>
			{shownModal===MODAL.ORDER_REFUND && <Modal.Content>
				{pending_refunds.length>0 && (
					<Message size="small" type="info" header="Reembolso pendiente" text="Ya existen reembolsos no completados de esta orden. Si se realiza otro reembolso se cancelarán los reembolsos no completados actuales." style={{ marginBottom: 10, marginTop: -5 }} />
				)}
				{/* <Items
					selectable
					data={order.tickets.filter(a=>a.paid && !a.cancelled)}
					valueExtractor={a=>a.ticket_id}
					selected={ticketsSelected}
					onToggle={setTicketsSelected}
					toggle
					render={(a)=><TicketItem ticket={a} price />} 
				/> */}
				<Table selectable>
					<Table.Head title="Boletos" />
					<Table.Body>
						{order.tickets.filter(a=>a.paid && !a.cancelled).map(a=>{
							var selected = ticketsSelected.indexOf(a.ticket_id)!=-1;
							return <Table.Row onClick={bindClick(setTicketsSelected, toggleArray(ticketsSelected, a.ticket_id))}>
								<Table.Cell collapsing style={{ paddingRight: 0 }}>
									<Icon style={{ margin: 0 }} size={18} name={selected ? 'circle-check' : 'circle'} solid={selected} />
								</Table.Cell>
								<Table.Cell>
									<div style={{ fontWeight: 'bold' }}>{a.ticket_hash}</div>
									<div style={{ fontSize: 14 }}>{a.section_name} - {a.price_name}</div>
								</Table.Cell>
							</Table.Row>
						})}
					</Table.Body>
				</Table>
				<Input 
					label="Correo electrónico" 
					comment={order.email && order.email.length>=5 ? 'Ya que la orden ya tiene un correo electrónico asignado no se puede enviar a otra dirección de correo.' : null} 
					value={order.email || refundForm.email} 
					readonly={!!order.email && order.email.length>5} 
					onChange={onRefundFormChange('email')} 
					style={{ marginTop: 5 }} 
				/>
				<div style={{ height: 5 }} />
				{user_access.refund_complete && refund_commission_amount>0 && (
					<Checkbox label={`Reembolsar $${addCommas(refund_commission_amount)} de comisión`} style={{ marginTop: 5 }} checked={!!refundForm.commission} onChange={onRefundFormChange('commission')} />
				)}
				{user_access.refund_complete && total_payment_cost>0 && (
					<Checkbox label={`Reembolsar $${addCommas(total_payment_cost)} de forma de pago`} style={{ marginTop: 5 }} checked={!!refundForm.payment} onChange={onRefundFormChange('payment')} />
				)}
				{user_access.refund_complete && order.delivery_cost>0 && (
					<Checkbox label={`Reembolsar $${addCommas(order.delivery_cost)} de entrega`} style={{ marginTop: 5 }} checked={!!refundForm.delivery} onChange={onRefundFormChange('delivery')} />
				)}
				{user_access.refund_fake && refund_total>0 && (
					<Checkbox label={'Solo marcar como reembolsada'} style={{ marginTop: 5 }} checked={!!refundForm.fake} onChange={onRefundFormChange('fake')} />
				)}
				{user_access.refund_fake && refundForm.fake && (
					<Checkbox label={'Enviar correo a cliente'} style={{ marginTop: 5 }} checked={!!refundForm.send_email} onChange={onRefundFormChange('send_email')} />
				)}
				{refund_total>0 && (
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Table className="basic totals">
							<Table.Body>
								<Table.Row data={['Boletos', addCommas(refund_tickets_amount)]} />
								{(refundForm && refundForm.commission) ? (
									<Table.Row data={['Comisión', addCommas(refund_commission_amount)]} />
								) : null}
								{(refundForm && refundForm.payment) ? (
									<Table.Row data={['Comisión', addCommas(total_payment_cost)]} />
								) : null}
								{(refundForm && refundForm.delivery) ? (
									<Table.Row data={['Comisión', addCommas(order.delivery_cost)]} />
								) : null}
								<Table.Row data={['Reembolso', addCommas(refund_total)]} />
							</Table.Body>
						</Table>
					</div>
				)}
				{refund_total>0 && (
					<Message type="info" style={{ marginTop: 10 }}>
						{user_access.refund_fake && refundForm.fake ? (
							'Se creará un reembolso con la configuración seleccionada y se marcará como completado.' + (
								refundForm.send_email ? ' Se le enviará un correo a cliente de que su orden fue reembolsada.' : ''
							)
						) : (
							'Se le enviará un correo electrónico a el cliente para que pueda completar el reembolso. Si es necesario, se le pedirá a el cliente su información bancaria para realizar una transferencia bancaria.'
						)}
					</Message>
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Reembolsar" color="red" disabled={refund_total<=0} onClick={createRefund} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===MODAL.DELETE_CONCILIA && !!selected_concilia} onClose={bindClick(setShownModal)} size="mini">
			<Modal.Header>Eliminar conciliación</Modal.Header>
			{shownModal===MODAL.DELETE_CONCILIA && selected_concilia && <Modal.Content>
				<Header size="small" text="¿Eliminar conciliación?" subtext="Esto eliminará la concilicación seleccionada." style={{ marginTop: 0 }} />
				<Stat label="Conciliación eliminada" value={addCommas(selected_concilia.amount)} style={{ paddingBottom: 10 }} className="red" />
			</Modal.Content>}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text="Eliminar" color="red" onClick={deleteDeposit} />
			</Modal.Actions>
		</Modal>
		<UserLogModal open={!!ticket && shownModal===MODAL.TICKET_USER} onClose={bindClick(setShownModal)} user={ticket} />
		
		<Modal open={shownModal===MODAL.TICKET_HIDE} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>{ticket?.hidden ? 'Mostrar Boleto' : 'Esconder Boleto'}</Modal.Header>
			{!!ticket && (
				<Modal.Content>
					<Header text={ticket.hidden ? `Mostrar boleto` : `Esconder boleto`} subtext={`Esto ${ticket.hidden ? 'mostrara' : 'escondera'} el boleto ${ticket?.ticket_hash}`} />
					<Table details style={{ marginBottom: 15 }}>
						<Table.Head title="Boleto" titleSize="small" />
						<Table.Body>
							<Table.Row data={['Boleto', ticket.ticket_hash]} />
							<Table.Row data={['Evento', ticket.event_name]} />
							<Table.Row data={['Fecha', moment.unix(ticket.ticket_date || ticket.date).format('DD/MMM/YY HH:mm')]} />
							<Table.Divider />
							<Table.Row data={['Sección', ticket.section_name]} />
							<Table.Row data={['Producto', ticket.price_name]} />
							{!!ticket.seat_id && (
								<Table.Row data={['Butaca', `${ticket.seat_section || ''} ${formatSeatNumber(ticket.seat_row, ticket.seat_number)}`]} />
							)}
							<Table.Row data={['Precio', addCommas(ticket.ticket_cost+ticket.commission)]} />
						</Table.Body>
					</Table>
					<Message>
						Los boletos escondidos no saldrán en reportes dentro del sistema.
						Estos serán contabilizados solo dentro de las estadisticas del administrador de AREMA, y solo se mostrarán a administradores con acceso para ver estos boletos.
					</Message>
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
				<Button text={ticket?.hidden ? "Mostrar" : "Esconder"} color="red" onClick={onHideTicket} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===MODAL.TICKET_SELECT} onClose={bindClick(setShownModal, null)}>
			<Modal.Header>{select_ticket_modal?.header}</Modal.Header>
			{select_ticket_modal && <Modal.Content>
				<Header text={select_ticket_modal.header} subtext={select_ticket_modal.subheader} />
				<Table fitted style={{ marginTop: 20 }} className={'first'}>
					<Table.Head
						data={[['', 'ID', 'Evento', 'Sección', 'Producto', 'Butaca', 'Usos', 'Precio']]}
					/>
					<Table.Body>
						{select_ticket_modal.tickets.map(a=>{
							var selected = ticketsSelected.indexOf(a.ticket_id)!==-1;
							var selectable = true;
							if(selectTicketType===SelectTicketType.HIDE_TICKETS){
								selectable = select_ticket_modal.selected.length==0 || (!select_ticket_modal.hiding)==a.hidden;
							}

							return <Table.Row selectable={selectable} key={`SLTK-${a.ticket_id}`} onClick={selectable ? bindClick(setTicketsSelected, toggleArray(ticketsSelected, a.ticket_id)) : null} style={{
								color: !selectable ? 'gray' : 'black'
							}}>
								<Table.Cell collapsing style={{ paddingRight: 0 }}>
									<Icon name={!selectable ? 'minus' : (selected ? 'circle-check' : 'circle')} solid={!selectable || !!selected} style={{ marginRight: 0 }} />
								</Table.Cell>
								<Table.Cell collapsing>{a.ticket_id}</Table.Cell>
								<Table.Cell>{a.event_name}</Table.Cell>
								<Table.Cell>{a.section_name}</Table.Cell>
								<Table.Cell>{a.price_name}</Table.Cell>
								<Table.Cell collapsing>
									{!!a.hidden && (
										<Icon name="eye-slash" color="brown" />
									)}
									{a.seat_id ? (
										formatSeatNumber(a.seat_row, a.seat_number)
									) : <Icon name="minus" color="gray" />}
								</Table.Cell>
								<Table.Cell collapsing style={{ color: a.uses>=1 ? 'brown' : 'black' }}>
									{a.uses>=1 && (
										<Icon name="exclamation-triangle" color="brown" />
									)}
									{a.uses}
								</Table.Cell>
								<Table.Cell collapsing>
									{!!a.gift_id ? (
										<Icon name="gift" />
									) : (
										addCommas(a.ticket_cost+a.commission)
									)}
								</Table.Cell>
							</Table.Row>
						})}
					</Table.Body>
				</Table>
				{selectTicketType!==SelectTicketType.REVIVE_TICKETS && (
					<Stat value={addCommas(select_ticket_modal?.selected?.length, false)} label={t('common.tickets', { count: select_ticket_modal?.selected?.length })} />
				)}
				{select_ticket_modal?.confirm && (
					<Checkbox label={select_ticket_modal?.confirm_text} checked={confirmCheck} onChange={setConfirmCheck} style={{ marginTop: 15 }} />
				)}
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal, null)} />
				<Button text={t('common.continue')} color={'black'} onClick={select_ticket_modal?.onComplete} disabled={!select_ticket_modal || !select_ticket_modal?.selected || select_ticket_modal?.selected.length==0 || (!!select_ticket_modal.confirm && !confirmCheck)} />
			</Modal.Actions>
		</Modal>
		
		<UpdateTicketDateModal
			title={`Cambio de fecha: ${ticket?.ticket_hash}`}
			open={!!ticket && shownModal===MODAL.TICKET_MOVE_DATE} 
			dateId={ticket?.date_id}
			eventId={ticket?.event_id}
			onClose={bindClick(setShownModal)}
			priceId={ticket?.price_id}
			sectionId={ticket?.section_id}
			ticketId={ticket?.ticket_id}
			onSave={onTicketPriceUpdate}
		/>
		
		<UpdateSeatTicketModal 
			title="Selecciona un Asiento"
			open={!!ticket && shownModal===MODAL.TICKET_UPDATE_SEAT} 
			onClose={bindClick(setShownModal)}
			eventId={ticket?.event_id}
			dateId={ticket?.date_id} 
			sectionId={ticket?.section_id} 
			seatId={ticket?.seat_id}
			onSave={onSaveNewTicketSeat}
		/>

		<PaymentInfoModal 
			editable={user_access.EDIT_PAYMENT_AMOUNTS}
			open={shownModal===MODAL.PAYMENT_INFO} 
			onPaymentEdit={onPaymentChange}
			onClose={bindClick(setShownModal)}
			onPaymentPaid={onPaymentPaid}
			paymentId={paymentIdSelected}
		/>
		
		{!!order && (
			<OrderInvoicesModal 
				order_id={order.order_id} 
				order_hash={order.order_hash}
				open={shownModal===MODAL.ORDER_INVOICES} 
				onClose={bindClick(setShownModal)} 
			/>
		)}

		{!!order && (
			<LocationModal 
				location_id={order.delivery_location} 
				open={shownModal===MODAL.ORDER_LOCATION} 
				onClose={bindClick(setShownModal)} 
				onLocationEdit={onLocationChange}
				editable={true}
			/>
		)}

		{!!order && (
			<OrderEmailsModal 
				order_id={order.order_id} 
				order_hash={order.order_hash}
				open={shownModal===MODAL.ORDER_EMAILS} 
				onClose={bindClick(setShownModal)} 
			/>
		)}

		{!!order && (
			<TicketPrintsModal
				ticketId={ticket?.ticket_id}
				ticketHash={ticket?.ticket_hash}
				open={!!ticket && shownModal===MODAL.TICKET_PRINTS}
				onClose={bindClick(setShownModal)}
			/>
		)}
	</div>
}

export default OrderView;